import {
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  BadgeIcon,
  BookOpen,
  CheckCircle,
  CheckIcon,
  CircleCheckIcon,
  KeyIcon,
  ListCollapseIcon,
  UsersIcon,
  UserIcon,
  Upload,
  UserPlus,
  UserMinus,
  MailCheck,
  Eye,
  XIcon,
  XCircle,
  Send,
  CalendarClock,
  AlertTriangle,
  HelpCircle,
  History,
} from "lucide-react";
import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { FiColumns } from "react-icons/fi";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
  useBlocker,
} from "react-router-dom";
import { MF_ACCESS_REVIEWS } from "state/members/accessReviews";
import { MF_OPTIONS } from "state/members/options";
import { members } from "state/members/member";
import ResourceIcon from "./ResourceIcon";
import { lunaSuggestedAccountIds } from "./lunaAlignment";
import ReviewAccountsTable from "./ReviewAccountsTable";
import ReviewModal from "./Table/ReviewModal";
import FilterBar, { FilterCondition, FilterType } from "./components/FilterBar";
import FilterChips from "./components/FilterChips";
import AddAccountModal from "./components/AddAccountModal";
import LunaAnalysisModal from "./components/LunaAnalysisModal";
import ReviewStateAlert, {
  hasReviewStateBanner,
} from "./components/ReviewStateAlert";
import StickyNavigator from "./components/StickyNavigator";
import EvidencePanel, { type EvidenceUpload } from "./components/EvidencePanel";
import FixPanel from "./components/FixPanel";
import FixIncorrectDataModal from "./Table/FixIncorrectDataModal";
import AccountChooserModal from "./components/AccountChooserModal";
import QueryResponsesModal from "./components/QueryResponsesModal";
import QueryModal from "./components/QueryModal";
import { QueriedChipVisual, AnsweredChipVisual } from "./components/QueryChip";
import { keyframes } from "@emotion/react";

// One shared empty list, so a campaign that carries no outcome options doesn't
// hand the memo()'d accounts table a new array on every render. (PLO-5463)
const EMPTY_OUTCOME_OPTIONS: MFCampaign["outcomeOptions"] = [];

// Subtle amber glow to draw the eye to answered query responses. Respects
// prefers-reduced-motion (the button keeps its static amber styling).
const responsePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45); }
  70% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
`;
import ProgressMeter from "./components/ProgressMeter";
import ReviewStatusPill from "./components/ReviewStatusPill";
import RevisionChip from "./components/RevisionChip";
import OverdueBlip from "./components/OverdueBlip";
import InstructionsCallout from "./components/InstructionsCallout";
import { useInstructionsCollapsed } from "./useInstructionsCollapsed";
import { useReviewColumnPrefs } from "./useReviewColumnPrefs";
import { appDisplayName } from "modules/apps/tenants";
import RequestExtensionModal from "./components/RequestExtensionModal";
import DeadlineExtensionBanner from "./components/DeadlineExtensionBanner";
import {
  AppLogo,
  Button,
  Counter,
  LunaMark,
  Menu as DsMenu,
  MenuItem as DsMenuItem,
  Dropdown,
  LunaBanner,
  SearchInput,
  SegmentedControl,
} from "components/ds";
import {
  applicationInstructionText,
  descriptionText,
  deriveReviewState,
  isSubmissionPending,
  isDecisionLocked,
  isCycleEnded,
  reviewIsOverdue,
  reversibleOutcomesFor,
  noOptimisticSubmit,
  optimisticSubmitRecorded,
  optimisticSubmitObserved,
  type OptimisticSubmit,
  stageBadgeLabel,
  reReviewBadge,
  reReviewSentence,
  hideReviewedEnabled,
  hasRemovedAccounts,
  isMultiStageContext,
  currentStageCounts,
  emptyScopeBanner,
  submitConfirmCopy,
  canSubmitReview,
  SUBMIT_DECISIONS_LABEL,
  allowsUndecidedSubmission,
  hasNothingInScope,
  partitionSettledByAccount,
  bulkCorrectionSummary,
  appliedCorrectionCount,
  isSoftLocked,
  shouldConfirmLeave,
  leaveConfirmCopy,
  armSubmitPrompt,
  idleSubmitPrompt,
  submitPromptDue,
  type SubmitPrompt,
  meterIsComplete,
  reviewStatePillKind,
  startedAgoLabel,
  dueDayLabel,
  daysUntil,
  nextSplitPanel,
  resolveMountedPanel,
  canRequestDeadlineExtension,
  effectiveReviewDueDate,
  deadlineExtensionBannerState,
  reviewAccountDisplayName,
  accountsSelectableForRemoval,
  queryResponseEntries,
  generalQueryEntries,
  hasOpenGeneralQuery,
  hasAnsweredGeneralQuery,
  flaggedNoteForSubmission,
  SUGGESTION_BUCKET_LABEL,
  reconcileEntitlementColumns,
  entitlementTypeColumn,
  hasRealEntitlementTypes,
  SELECTABLE_REVIEW_COLUMNS,
  REVIEW_COLUMN_LABELS,
  hasIndirectAccountRow,
  withAccessKindColumn,
  showsResourceColumn,
  withResourceColumn,
  reviewResourceOptions,
  currentEntitlementIds,
  grantingGroups,
  pendingRemovalCount,
  resolveReviewView,
  type SplitPanel,
  type StagedCorrection,
} from "./reviewLogic";
import { memberErrorMessage } from "state/members/memberErrors";
import { Switch as DsSwitch, ConfirmModal } from "components/ds";
import {
  useListEvidence,
  useUploadEvidence,
  useGetEvidenceDownloadUrl,
  useDeleteEvidence,
  useGetEvidenceAnalysis,
} from "state/members/accessReviewsEvidence";
import useFilters from "./filters/useFilters";
import { getAvailableFilterTypes } from "./filters/filterRegistry";
import CardCarousel from "components/CardCarousel";
import { MFCampaign, MFReviewAccount } from "state/members/accessReviews";
import StatementCard from "pages/Members/Attestations/StatementCard";
import {
  isCombinedReview,
  isEntitlementOnly,
} from "pages/Members/Attestations/signOff";

const DropdownSelector = memo(
  ({
    label,
    value,
    icon,
    options,
    onChange,
  }: {
    label: string;
    value: string;
    icon: any;
    options: { value: string; label: string; icon?: any }[];
    onChange: (value: string) => void;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedLabel =
      options.find((opt) => opt.value === value)?.label || value;

    // Close on outside click — the DS Menu owns in-menu keyboard nav and
    // Escape, the trigger owns click-away dismissal.
    const wrapRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!isOpen) return;
      const onDown = (e: MouseEvent) => {
        if (!wrapRef.current?.contains(e.target as Node)) setIsOpen(false);
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [isOpen]);

    return (
      <Box position="relative" ref={wrapRef}>
        <Dropdown
          size="sm"
          label={label}
          value={selectedLabel}
          icon={<Icon as={icon} boxSize={3.5} />}
          open={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        />

        {isOpen && (
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            left={0}
            minW="100%"
            zIndex="dropdown"
          >
            <DsMenu
              ariaLabel={label}
              autoFocus
              onClose={() => setIsOpen(false)}
            >
              {options.map((option) => (
                <DsMenuItem
                  key={option.value}
                  selection="single"
                  selected={option.value === value}
                  leadingIcon={
                    option.icon ? (
                      <Icon as={option.icon} boxSize={4} />
                    ) : undefined
                  }
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {option.label}
                </DsMenuItem>
              ))}
            </DsMenu>
          </Box>
        )}
      </Box>
    );
  },
);

const ReviewDetail: React.FC = () => {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { data: reviewDetails, isLoading } =
    MF_ACCESS_REVIEWS.useGetReviewDetails(reviewId || "");
  const { mutate: reviewAccounts, isLoading: isReviewing } =
    MF_ACCESS_REVIEWS.useReviewCampaignAccounts(reviewId || "");

  // Get current member
  const currentMember = members.useGetMe();

  // Check if current member is a resource owner
  const isResourceOwner = useMemo(() => {
    if (!currentMember?.id || !reviewDetails?.review?.resource?.owners) {
      return false;
    }
    const ownerMemberIds = reviewDetails.review.resource.owners.map(
      (owner: any) => owner.memberId,
    );
    return ownerMemberIds.includes(currentMember.id);
  }, [currentMember?.id, reviewDetails?.review?.resource?.owners]);

  // Always call hooks at the top level, never conditionally
  // Support both campaignId (legacy) and campaignCycleId (new)
  const campaignId =
    reviewDetails?.review?.campaignId ||
    reviewDetails?.review?.campaignCycleId ||
    "";
  const { data: campaignDetails } =
    MF_ACCESS_REVIEWS.useGetCampaignDetails(campaignId);

  // (prev/next resource-review navigation now lives in StickyNavigator, driven
  // by campaignDetails.reviews + reviewLogic's navigatorNeighbours.)

  const accountRows = MF_ACCESS_REVIEWS.useListReviewAccountRows(
    reviewId || "",
  );
  const flatReviewAccounts: MFReviewAccount[] = useMemo(
    () => accountRows ?? [],
    [accountRows],
  );

  // Get AI suggestions from the API
  const {
    data: aiSuggestions,
    isLoading: aiSuggestionsLoading,
    refetch: refetchAISuggestions,
    status: aiStatus,
    errorMessage: aiErrorMessage,
    isGenerating: aiIsGenerating,
  } = MF_ACCESS_REVIEWS.useGetAISuggestions(reviewId || "");

  // Debug logging
  // Fallback to empty structure if no data
  const effectiveAISuggestions = aiSuggestions || {
    auto_approve: [],
    auto_reject: [],
    needs_review: [],
  };

  // Check if Luna is still generating suggestions
  const isLunaGenerating = aiIsGenerating || aiStatus === "pending";

  // Calculate total accounts for each category
  const getTotalAccountsCount = (
    suggestions: Array<{ account_ids: string[]; reasoning: string | null }>,
  ) => {
    return suggestions.reduce(
      (total, item) => total + item.account_ids.length,
      0,
    );
  };

  // Find the first tab with results for default selection
  const getDefaultTab = () => {
    if (
      effectiveAISuggestions.auto_approve.length > 0 &&
      getTotalAccountsCount(effectiveAISuggestions.auto_approve) > 0
    )
      return "auto-approve";
    if (
      effectiveAISuggestions.auto_reject.length > 0 &&
      getTotalAccountsCount(effectiveAISuggestions.auto_reject) > 0
    )
      return "auto-reject";
    if (
      effectiveAISuggestions.needs_review.length > 0 &&
      getTotalAccountsCount(effectiveAISuggestions.needs_review) > 0
    )
      return "needs-review";
    return "auto-approve"; // fallback
  };

  // Get filter options from options service
  const options = MF_OPTIONS.useListOptions();

  // State for filters
  const [advancedFilters, setAdvancedFilters] = useState<FilterCondition[]>([]);

  // Use our centralized filter system
  const {
    activeFilters,
    addFilter: addFilterInternal,
    removeFilter: removeFilterInternal,
    clearAllFilters: clearAllFiltersInternal,
    getOptionsForFilter,
  } = useFilters(advancedFilters);

  // Wrapper functions to update both our local state and the filter hook
  const handleAddFilter = (newFilter: FilterCondition) => {
    // Ensure the filter has an ID for consistent removal
    const filterWithId = {
      ...newFilter,
      id: newFilter.id || `filter-${Date.now()}`,
    };
    setAdvancedFilters((prev) => [...prev, filterWithId]);
    addFilterInternal(filterWithId);
  };

  const handleRemoveFilter = (filter: FilterCondition) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== filter.id));
    removeFilterInternal(filter.id);
  };

  const handleClearAllFilters = () => {
    setAdvancedFilters([]);
    clearAllFiltersInternal();
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  // Null until the reviewer picks one, so a combined review can default to
  // "person" once its cycle has loaded.
  const [groupingChoice, setSelectedGrouping] = useState<string | null>(null);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  // Hide-reviewed toggle for the all-entitlements table (review STATE, split
  // from the review STATUS filter above). Only meaningful once something's
  // reviewed — gated at the toggle's render site.
  const [hideReviewed, setHideReviewed] = useState(false);
  // "Hide removed accounts" (PLO-4806) — default ON: once an account is decided
  // removed / already-removed it's done work that clutters the live list. Display
  // only; the server-derived completion counts are unaffected. Composes with the
  // hide-reviewed toggle (both on = both filters).
  const [hideRemoved, setHideRemoved] = useState(true);
  // Collapsed state for the per-application Instructions callout, persisted
  // per review — default expanded, collapsing swaps it for a compact "View
  // instructions" trigger next to the other header actions.
  const { isInstructionsCollapsed, setInstructionsCollapsed } =
    useInstructionsCollapsed(reviewId || "");
  // Read once — this only gates the collapse/expand animation duration below,
  // not a live-toggling concern worth a matchMedia listener.
  const [prefersReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  // Check if all accounts in each tab/card are complete and count completed
  const completionStatus = useMemo(() => {
    if (
      !accountRows ||
      reviewDetails?.accountSetStatus === "revision_requested"
    ) {
      return {
        tabs: {
          autoApprove: false,
          autoReject: false,
          needsReview: false,
        },
        tabCounts: {
          autoApprove: 0,
          autoReject: 0,
          needsReview: 0,
        },
        cards: {} as Record<string, boolean>,
      };
    }

    const accountsIn = (accountIds: string[]) => {
      const ids = new Set(accountIds);
      return accountRows.filter((acc) => ids.has(acc.id));
    };

    const checkComplete = (accountIds: string[]) => {
      if (!accountIds || accountIds.length === 0) return false;

      const accounts = accountsIn(accountIds);

      return (
        accounts.length > 0 &&
        accounts.every(
          (acc: any) =>
            acc.review !== null &&
            acc.review !== undefined &&
            acc.review !== "query",
        )
      );
    };

    const countCompleted = (accountIds: string[]) => {
      if (!accountIds || accountIds.length === 0) return 0;

      const accounts = accountsIn(accountIds);

      return accounts.filter(
        (acc: any) =>
          acc.review !== null &&
          acc.review !== undefined &&
          acc.review !== "query",
      ).length;
    };

    const cardCompletion: Record<string, boolean> = {};

    // Check completion for each card in auto_approve
    effectiveAISuggestions.auto_approve.forEach((item, idx) => {
      cardCompletion[`auto-approve-${idx}`] = checkComplete(item.account_ids);
    });

    // Check completion for each card in auto_reject
    effectiveAISuggestions.auto_reject.forEach((item, idx) => {
      cardCompletion[`auto-reject-${idx}`] = checkComplete(item.account_ids);
    });

    // Check completion for each card in needs_review
    effectiveAISuggestions.needs_review.forEach((item, idx) => {
      cardCompletion[`needs-review-${idx}`] = checkComplete(item.account_ids);
    });

    return {
      tabs: {
        autoApprove: checkComplete(
          effectiveAISuggestions.auto_approve.flatMap(
            (item) => item.account_ids,
          ),
        ),
        autoReject: checkComplete(
          effectiveAISuggestions.auto_reject.flatMap(
            (item) => item.account_ids,
          ),
        ),
        needsReview: checkComplete(
          effectiveAISuggestions.needs_review.flatMap(
            (item) => item.account_ids,
          ),
        ),
      },
      tabCounts: {
        autoApprove: countCompleted(
          effectiveAISuggestions.auto_approve.flatMap(
            (item) => item.account_ids,
          ),
        ),
        autoReject: countCompleted(
          effectiveAISuggestions.auto_reject.flatMap(
            (item) => item.account_ids,
          ),
        ),
        needsReview: countCompleted(
          effectiveAISuggestions.needs_review.flatMap(
            (item) => item.account_ids,
          ),
        ),
      },
      cards: cardCompletion,
    };
  }, [accountRows, effectiveAISuggestions, reviewDetails?.accountSetStatus]);

  // Modal and review state for bulk actions
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [reviewAction, setReviewAction] = useState<string | null>(null);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(
    null,
  );
  const [outOfScopeReason, setOutOfScopeReason] = useState("");
  const [note, setNote] = useState("");
  const [suggestionReasoning, setSuggestionReasoning] = useState<string | null>(
    null,
  );
  const [bulkActionOnComplete, setBulkActionOnComplete] = useState<
    (() => void) | null
  >(null);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [isLunaModalOpen, setIsLunaModalOpen] = useState(false);
  // Transient evidence uploads (in-flight + failed) — rendered as cards inside
  // the evidence panel. Successful uploads drop off here and reappear via the
  // persisted evidence-files list on refetch (design D — no upload modal).
  const [evidenceUploads, setEvidenceUploads] = useState<EvidenceUpload[]>([]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  // Opens the submit modal once the reviewer's own decision finishes the
  // review (PLO-6222). See `SubmitPrompt`.
  const [submitPrompt, setSubmitPrompt] = useState<SubmitPrompt>(() =>
    idleSubmitPrompt(reviewId),
  );
  // Called after anything that can finish the review: a decision, or marking
  // an account as not existing at source.
  const armPrompt = useCallback(
    () => setSubmitPrompt((prev) => armSubmitPrompt(prev, reviewId)),
    [reviewId],
  );
  // Deadline-extension request modal (PLO-4856).
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  // Split-panel view: at most one of the evidence / corrections (fix) panels is
  // open at a time. The single-open invariant + toggle live in reviewLogic
  // (nextSplitPanel / resolveMountedPanel); this holds the current selection.
  const [splitPanel, setSplitPanel] = useState<SplitPanel>(null);
  const toggleSplitPanel = useCallback(
    (panel: Exclude<SplitPanel, null>) =>
      setSplitPanel((current) => nextSplitPanel(current, panel)),
    [],
  );
  // Fixes auto-save and persist server-side (PLO-4803): the modal's "Apply
  // fixes" persists immediately, and the applied corrections come back on the
  // accounts payload's `entitlementCorrections` field — the corrections panel is
  // single-sourced from there, so there's no client-side log to keep.
  const updateAccountEntitlements =
    MF_ACCESS_REVIEWS.useUpdateAccountEntitlements();
  // Header-menu "Edit an account's entitlements" has no row context, so it opens
  // an account chooser first; picking one opens the Fix-incorrect-data modal.
  const [isFixChooserOpen, setIsFixChooserOpen] = useState(false);
  const [fixTargetAccountId, setFixTargetAccountId] = useState<string | null>(
    null,
  );
  // "Remove a user who doesn't exist" (PLO-4803): pick an account, confirm, then
  // mark it already-removed / access revoked at source.
  const [isRemoveChooserOpen, setIsRemoveChooserOpen] = useState(false);
  const [removeTargetAccountId, setRemoveTargetAccountId] = useState<
    string | null
  >(null);
  const removeNonExistentAccount =
    MF_ACCESS_REVIEWS.useRemoveNonExistentAccount();

  // Apply corrections immediately: one full-replace call per account (the
  // endpoint replaces the set), tolerant of per-account failure via allSettled.
  // Succeeded accounts are logged as applied fixes and the panel opens.
  const applyCorrections = useCallback(
    async (corrections: StagedCorrection[]) => {
      if (corrections.length === 0) return;
      const ids = corrections.map((c) => c.accountId);
      const settled = await Promise.allSettled(
        corrections.map((c) =>
          updateAccountEntitlements.mutate(
            c.accountId,
            c.entitlementIds,
            c.newEntitlementNames,
          ),
        ),
      );
      const outcome = partitionSettledByAccount(ids, settled);
      // Applied corrections come back via the accounts refetch (the mutation
      // invalidates the review queries); just open the panel to reveal them.
      if (outcome.succeeded.length > 0) {
        setSplitPanel("corrections");
      }
      toast({
        title:
          outcome.failed.length === 0
            ? "Fixes applied"
            : "Some fixes couldn't be applied",
        description: bulkCorrectionSummary(outcome),
        status: outcome.succeeded.length > 0 ? "success" : "error",
        duration: 4000,
        isClosable: true,
        position: "bottom-right",
      });
    },
    [updateAccountEntitlements, toast],
  );
  // Ready-to-submit soft-lock: when a review is fully reviewed but unsubmitted
  // the table is browse-only behind a submit CTA. Clicking "Edit" flips this
  // override so the reviewer can change decisions again.
  const [editOverride, setEditOverride] = useState(false);
  // Optimistic submission state: whether one is outstanding, plus the last
  // status seen from the server so it can expire itself. See `OptimisticSubmit`.
  const [optimisticSubmit, setOptimisticSubmit] = useState<OptimisticSubmit>(
    () => noOptimisticSubmit(null),
  );
  // Set of statement IDs the reviewer has ticked in the inline attestation
  // step (only used when cycle.attestationMode === "reviewer_per_set"). Reset
  // every time the modal opens so a previously-aborted submit doesn't leak
  // stale confirmations across submits.
  const [confirmedStatementIds, setConfirmedStatementIds] = useState<
    Set<string>
  >(() => new Set());
  const [markAsRemoved, setMarkAsRemoved] = useState(false);
  // Entitlement flagging state
  const [flaggedSnapshotIds, setFlaggedSnapshotIds] = useState<string[]>([]);

  // Max number of entitlement type columns to display (rest go into "Other")
  const MAX_ENTITLEMENT_TYPE_COLUMNS = 4;

  // Compute unique entitlement types from all accounts, sorted by frequency
  const uniqueEntitlementTypes = useMemo(() => {
    // Count frequency of each type
    const typeCounts = new Map<string, number>();
    flatReviewAccounts.forEach((acc) => {
      (acc.entitlements || []).forEach((ent) => {
        const type = ent.snapshotEntitlementType || "Other";
        typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
      });
    });

    // Sort by frequency (most common first), but keep "Other" separate. Ties
    // break by name so the column order never depends on the rows' order.
    const types = Array.from(typeCounts.keys()).filter((t) => t !== "Other");
    types.sort(
      (a, b) =>
        (typeCounts.get(b) || 0) - (typeCounts.get(a) || 0) ||
        a.localeCompare(b),
    );

    // Limit to max columns, group rest into "Other"
    const hasOther = typeCounts.has("Other");
    const displayTypes = types.slice(0, MAX_ENTITLEMENT_TYPE_COLUMNS);

    // If we have more types than max, or if there's already an "Other", add "Other" column
    if (types.length > MAX_ENTITLEMENT_TYPE_COLUMNS || hasOther) {
      displayTypes.push("Other");
    }

    return displayTypes;
  }, [flatReviewAccounts]);

  // Identity-stable stand-in for uniqueEntitlementTypes: the memo above is
  // keyed on the account rows, so it yields a brand-new array on every refetch even
  // when the types themselves are identical. Effects that react to the type set
  // must key on this, never on the array (PLO-5210).
  // NUL separator, not a space: type names contain spaces ("Team Hub &
  // Workspace Role"), so a space would let ["A B","C"] and ["A","B C"] share
  // a key and a real type change would go unnoticed.
  const entitlementTypesKey = uniqueEntitlementTypes.join("\u0000");

  // Whether the hide-reviewed toggle is meaningful (>= 1 account reviewed).
  const canHideReviewed = useMemo(
    () => hideReviewedEnabled(flatReviewAccounts),
    [flatReviewAccounts],
  );

  // Whether the hide-removed toggle is meaningful (>= 1 removed account).
  const canHideRemoved = useMemo(
    () => hasRemovedAccounts(flatReviewAccounts),
    [flatReviewAccounts],
  );

  // Stable single-element array for the header-launched fix modal so its seed
  // effect isn't re-run (wiping edits) on unrelated re-renders / refetches.
  const fixTargetAccounts = useMemo(
    () =>
      fixTargetAccountId
        ? flatReviewAccounts.filter((a) => a.id === fixTargetAccountId)
        : [],
    [fixTargetAccountId, flatReviewAccounts],
  );
  const removeTargetAccount = useMemo(
    () =>
      removeTargetAccountId
        ? (flatReviewAccounts.find((a) => a.id === removeTargetAccountId) ??
          null)
        : null,
    [removeTargetAccountId, flatReviewAccounts],
  );
  // Answered query responses awaiting the reviewer — account-scoped queries plus
  // answered review-scope (general) queries, sharing one list + "View responses"
  // count (PLO-4803).
  const queryResponses = useMemo(
    () => [
      ...queryResponseEntries(flatReviewAccounts),
      ...generalQueryEntries(reviewDetails?.generalQueries),
    ],
    [flatReviewAccounts, reviewDetails?.generalQueries],
  );
  const [isResponsesModalOpen, setIsResponsesModalOpen] = useState(false);

  // Raise-a-general-query (review-scope, account-less) modal state (PLO-4803).
  const [isGeneralQueryModalOpen, setIsGeneralQueryModalOpen] = useState(false);
  const [isRaisingGeneralQuery, setIsRaisingGeneralQuery] = useState(false);
  const raiseGeneralQuery = MF_ACCESS_REVIEWS.useRaiseGeneralQuery(
    reviewId || "",
  );
  const handleRaiseGeneralQuery = useCallback(
    async (note: string) => {
      setIsRaisingGeneralQuery(true);
      try {
        await raiseGeneralQuery.mutate(note);
        setIsGeneralQueryModalOpen(false);
        toast({
          title: "Query sent",
          description: "The campaign admin will respond to your review query.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom-right",
        });
      } catch (error: any) {
        toast({
          title: "Couldn't send the query",
          description: error?.message || "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
      } finally {
        setIsRaisingGeneralQuery(false);
      }
    },
    [raiseGeneralQuery, toast],
  );

  const handleRemoveNonExistent = useCallback(async () => {
    if (!removeTargetAccountId) return;
    try {
      await removeNonExistentAccount.mutate(removeTargetAccountId);
      toast({
        title: "Account removed",
        description:
          "Recorded as revoked. This access no longer exists at source.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      setRemoveTargetAccountId(null);
      armPrompt();
      // Surface the removal in the corrections panel (applied fix).
      setSplitPanel("corrections");
    } catch (error: any) {
      toast({
        title: "Couldn't remove the account",
        description: error?.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    }
  }, [removeTargetAccountId, removeNonExistentAccount, toast, armPrompt]);
  // Fix-button badge count = the panel's total: flagged-for-adjustment accounts
  // + accounts removed as not-in-source + applied entitlement corrections (the
  // last derived from the server's `entitlementCorrections` list).
  const recordedFixCount = useMemo(
    () =>
      flatReviewAccounts.filter(
        (a) =>
          (a.entitlements || []).some((e) => e.flaggedForAdjustment) ||
          (a.review as string) === "removed",
      ).length + appliedCorrectionCount(flatReviewAccounts),
    [flatReviewAccounts],
  );

  // Entitlements for the selected accounts, in the two shapes the review modal
  // needs: merged for the list it renders, and kept per account for judging
  // whether a decision empties an account (PLO-5461 — accounts on one resource
  // share entitlement ids, so the merged list cannot answer that).
  const {
    selectedAccountsEntitlements,
    selectedAccountsEntitlementIds,
    selectedGrantingGroups,
    selectedPendingRemovals,
  } = useMemo(() => {
    if (selectedAccounts.length === 0) {
      return {
        selectedAccountsEntitlements: [],
        selectedAccountsEntitlementIds: [],
        selectedGrantingGroups: { groups: [], separate: false },
        selectedPendingRemovals: 0,
      };
    }

    // Set, not Array#includes: a bulk action on a large review selects every
    // account, and the linear scan then ran once per account (PLO-5463).
    const selectedIds = new Set(selectedAccounts);
    const selected = flatReviewAccounts.filter((acc) =>
      selectedIds.has(acc.id),
    );

    // Deduplicated by snapshotEntitlementId (the entitlement itself, not the
    // per-user grant id).
    const merged = new Map<string, any>();
    selected.forEach((acc: any) => {
      (acc.entitlements || []).forEach((ent: any) => {
        if (
          ent.snapshotEntitlementId &&
          !merged.has(ent.snapshotEntitlementId)
        ) {
          merged.set(ent.snapshotEntitlementId, ent);
        }
      });
    });

    return {
      selectedAccountsEntitlements: Array.from(merged.values()),
      selectedAccountsEntitlementIds: selected.map(currentEntitlementIds),
      selectedGrantingGroups: grantingGroups(selected),
      selectedPendingRemovals: pendingRemovalCount(selected),
    };
  }, [flatReviewAccounts, selectedAccounts]);

  // Evidence hooks
  const { evidenceFiles, refetch: refetchEvidence } = useListEvidence(
    reviewId || "",
  );
  const uploadEvidence = useUploadEvidence();
  const deleteEvidence = useDeleteEvidence();
  const getDownloadUrl = useGetEvidenceDownloadUrl();
  const accountSetId = reviewDetails?.accountSetId || "";
  const accountSetStatus = reviewDetails?.accountSetStatus || null;
  const cycleStatus = reviewDetails?.cycleStatus;
  const accountSetRevisionMessage =
    reviewDetails?.accountSetRevisionMessage || null;
  const { analysis: existingAnalysis } = useGetEvidenceAnalysis(accountSetId);
  // Review-scoped batch submit: one submit + one attestation
  // covers EVERY submittable set the member owns in this review (the merged
  // list the page already shows), not just the single "current" account set.
  const { mutate: submitReviewForApproval, isLoading: isSubmitting } =
    MF_ACCESS_REVIEWS.useSubmitReview();

  // Evidence handlers
  const handleEvidenceDelete = async (fileId: string, filename: string) => {
    try {
      await deleteEvidence.mutateAsync({
        reviewId: reviewId || "",
        fileId,
      });
      refetchEvidence();
      toast({
        title: "Evidence deleted",
        description: `${filename} has been removed`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "Failed to delete evidence file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEvidenceDownload = async (fileId: string) => {
    try {
      const response = await getDownloadUrl.mutateAsync({
        reviewId: reviewId || "",
        fileId,
      });
      if (response?.data?.download_url) {
        window.open(response.data.download_url, "_blank");
      }
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to get download URL",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Upload evidence directly from the panel (design D — no modal). Each file is
  // uploaded sequentially; success drops the transient card (the persisted list
  // takes over on refetch), failure leaves a "Failed" card with retry/remove.
  const uploadEvidenceFile = useCallback(
    async (item: EvidenceUpload) => {
      try {
        await uploadEvidence.mutateAsync({
          reviewId: reviewId || "",
          file: item.file,
        });
        setEvidenceUploads((prev) => prev.filter((u) => u.id !== item.id));
        refetchEvidence();
      } catch (error) {
        setEvidenceUploads((prev) =>
          prev.map((u) =>
            u.id === item.id
              ? {
                  ...u,
                  status: "error",
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : u,
          ),
        );
      }
    },
    [reviewId, uploadEvidence, refetchEvidence],
  );

  const handleEvidenceUpload = useCallback(
    (fileList: File[]) => {
      const maxSizeBytes = 4 * 1024 * 1024;
      const valid = fileList.filter((f) => {
        if (f.size > maxSizeBytes) {
          toast({
            title: "File too large",
            description: `${f.name} is larger than 4MB`,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        return true;
      });
      if (valid.length === 0) return;

      const items: EvidenceUpload[] = valid.map((file, i) => ({
        id: `${Date.now()}-${i}-${file.name}`,
        name: file.name,
        size: file.size,
        status: "uploading",
        file,
      }));
      setEvidenceUploads((prev) => [...prev, ...items]);
      items.forEach((item) => void uploadEvidenceFile(item));
    },
    [toast, uploadEvidenceFile],
  );

  const handleRetryUpload = useCallback(
    (id: string) => {
      setEvidenceUploads((prev) => {
        const target = prev.find((u) => u.id === id);
        if (target) {
          void uploadEvidenceFile({ ...target, status: "uploading" });
        }
        return prev.map((u) =>
          u.id === id ? { ...u, status: "uploading", error: undefined } : u,
        );
      });
    },
    [uploadEvidenceFile],
  );

  const handleDismissUpload = useCallback((id: string) => {
    setEvidenceUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);
  // The reviewer's remembered view (PLO-5119): every column in `columnOptions`
  // on by default plus the pinned "actions", entitlements split by type when
  // this review's entitlements carry types, and whatever the reviewer picked
  // last time in preference to both.
  const {
    visibleColumns,
    setVisibleColumns,
    toggleColumn,
    splitEntitlementsByType,
    setSplitEntitlementsByType,
  } = useReviewColumnPrefs(hasRealEntitlementTypes(uniqueEntitlementTypes));

  // Reference to the columns dropdown to detect outside clicks
  const columnsRef = useRef<HTMLDivElement>(null);

  // Whether leaving the page should prompt a confirmation — ready-to-submit
  // and unsubmitted, or an unsaved Fix-incorrect-data draft (F2). Kept in a
  // ref so handleGoBack — defined above the derived reviewState — and the
  // mount-once beforeunload listener below can consult the latest value
  // without a stale closure.
  const leaveGuardRef = useRef(false);

  // Router-level leave guard (PLO-4803 finding P2): owns ALL leave
  // confirmations — the custom Back button and StickyNavigator prev/next
  // just call navigate() (see handleGoBack/handleNavigateReview below) and
  // let this blocker intercept, same as browser back/forward and any other
  // in-app link (sidebar, breadcrumbs, etc.).
  // Only fires on an actual page change (not query-param-only updates on this
  // same route). The function identity is allowed to change every render —
  // react-router re-registers it, so it always reads the current ref value.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      leaveGuardRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Browser refresh/tab-close bypasses the router entirely, so it needs its
  // own native guard. The browser renders its own prompt UI — we can't
  // restyle it — we can only opt in via preventDefault/returnValue.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (leaveGuardRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Close columns dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnsRef.current &&
        !columnsRef.current.contains(event.target as Node)
      ) {
        setIsColumnsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Available column options - includes dynamic entitlement type columns when split
  // is enabled.
  //
  // Access Level and Access Expires are deliberately absent. Access Level
  // rendered `account.accessLevel`, which the server filled from the same entitlement
  // names the Entitlements column already shows - a plain comma-joined duplicate of a
  // richer cell, and access level is not the model any more.
  //
  // Department and Job Title went the same way (PLO-5119): the Account cell
  // already renders "Department • Job title" under the member's name. Both are
  // still offered as Group by options and as filters, where they aren't
  // duplicating anything.
  // Derived from SELECTABLE_REVIEW_COLUMNS rather than re-listing the ids, so
  // the picker and the sanitizer's allowlist can't drift: the only special
  // case is the entitlements slot, which fans out into one option per type
  // while the splitter is on.
  const columnOptions = useMemo(
    () =>
      SELECTABLE_REVIEW_COLUMNS.flatMap((value) => {
        if (value !== "entitlements")
          return [{ value, label: REVIEW_COLUMN_LABELS[value] }];
        if (!splitEntitlementsByType || uniqueEntitlementTypes.length === 0)
          return [{ value, label: REVIEW_COLUMN_LABELS[value] }];
        return uniqueEntitlementTypes.map((type) => ({
          value: entitlementTypeColumn(type),
          label: type,
        }));
      }),
    [splitEntitlementsByType, uniqueEntitlementTypes],
  );

  // Update visible columns when the split toggle — or the set of entitlement
  // types — changes.
  //
  // Keyed on the type NAMES, not the array identity: uniqueEntitlementTypes is
  // recomputed from the account rows, so every refetch (submitting a decision is
  // one) hands us a fresh array holding the same types. entitlementSplitColumns
  // is idempotent and returns `prev` untouched when nothing actually changed,
  // so a refetch can no longer duplicate or reorder the columns (PLO-5210).
  useEffect(() => {
    setVisibleColumns((prev) =>
      reconcileEntitlementColumns(
        prev,
        uniqueEntitlementTypes,
        splitEntitlementsByType,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitEntitlementsByType, entitlementTypesKey]);

  const navigateBack = () => {
    const campaignId = (location.state as any)?.campaignId;
    // Preserve search params when navigating back
    const queryString = searchParams.toString();
    if (campaignId) {
      navigate(
        `/members/access-reviews/campaigns/${campaignId}${
          queryString ? `?${queryString}` : ""
        }`,
      );
    } else {
      navigate(
        `/members/access-reviews${queryString ? `?${queryString}` : ""}`,
      );
    }
  };

  // Ready-to-submit + unsubmitted, or an unsaved fix draft → the router-level
  // blocker above intercepts this navigate() and shows the leave-confirmation.
  const handleGoBack = () => {
    navigateBack();
  };

  // Navigating between resource reviews (StickyNavigator prev/next/dots) leaves
  // this review just like Back does, so it's guarded the same way.
  const handleNavigateReview = (id: string) => {
    navigate(`/members/access-reviews/reviews/${id}`);
  };

  // Handle bulk approve for accounts in a specific card
  const handleApproveAll = useCallback(
    (
      accountIds: string[],
      reasoning?: string,
      cardId?: string,
      onComplete?: () => void,
    ) => {
      if (accountIds.length === 0) return;
      // Defence-in-depth: even if a stale "Approve all" CTA is reached after
      // the set has been submitted/approved or the cycle ended, we refuse to
      // mutate.
      if (isDecisionLocked(optimisticSubmit, accountSetStatus, cycleStatus)) {
        return;
      }

      // Find the affirmative outcome from outcomeOptions (the one with standard_mapping "required")
      const outcomeOptions = campaignDetails?.campaign?.outcomeOptions;
      const affirmativeOutcome =
        outcomeOptions?.find((opt) => opt.standard_mapping === "required") ||
        outcomeOptions?.[0];
      const action = affirmativeOutcome?.standard_mapping || "required";

      // Set selected accounts and open modal for approval
      setSelectedAccounts(accountIds);
      setSelectedAccountName(null);
      setReviewAction(action);
      setOutOfScopeReason("");
      // Pre-populate note with suggestion reasoning if provided
      setNote(reasoning || "");
      setSuggestionReasoning(reasoning || null);
      setBulkActionOnComplete(() => onComplete || null);
      setMarkAsRemoved(false);
      openModal();
    },
    [
      openModal,
      campaignDetails,
      optimisticSubmit,
      accountSetStatus,
      cycleStatus,
    ],
  );

  // Handle bulk reject for accounts in a specific card
  const handleRejectAll = useCallback(
    (
      accountIds: string[],
      reasoning?: string,
      cardId?: string,
      onComplete?: () => void,
    ) => {
      if (accountIds.length === 0) return;
      if (isDecisionLocked(optimisticSubmit, accountSetStatus, cycleStatus)) {
        return;
      }

      // Find the negative outcome from outcomeOptions (the one with standard_mapping "not_required")
      const outcomeOptions = campaignDetails?.campaign?.outcomeOptions;
      const negativeOutcome =
        outcomeOptions?.find(
          (opt) => opt.standard_mapping === "not_required",
        ) || outcomeOptions?.[1];
      const action = negativeOutcome?.standard_mapping || "not_required";

      // Set selected accounts and open modal for rejection
      setSelectedAccounts(accountIds);
      setSelectedAccountName(null);
      setReviewAction(action);
      setOutOfScopeReason("");
      // Pre-populate note with suggestion reasoning if provided
      setNote(reasoning || "");
      setSuggestionReasoning(reasoning || null);
      setBulkActionOnComplete(() => onComplete || null);
      setMarkAsRemoved(false);
      openModal();
    },
    [
      openModal,
      campaignDetails,
      optimisticSubmit,
      accountSetStatus,
      cycleStatus,
    ],
  );

  // Submit the review after modal confirmation
  const submitReview = useCallback(() => {
    if (!reviewAction || selectedAccounts.length === 0) return;
    // Defence-in-depth: refuse to mutate accounts if the set has already
    // moved past the editable phase or the cycle ended. This guards against a
    // stale modal submitting after the user attested.
    if (isDecisionLocked(optimisticSubmit, accountSetStatus, cycleStatus)) {
      closeModal();
      return;
    }

    reviewAccounts({
      reviewId: reviewId || "",
      accountIds: selectedAccounts,
      review: reviewAction,
      outOfScopeReason:
        reviewAction === "out_of_scope" ? outOfScopeReason : undefined,
      note: note || undefined,
      markAsRemoved: markAsRemoved || undefined,
      // Entitlement flagging
      flaggedSnapshotIds:
        flaggedSnapshotIds.length > 0 ? flaggedSnapshotIds : undefined,
      flaggedNote: flaggedNoteForSubmission(note, flaggedSnapshotIds),
      // Every decision path lands here (a single row, a table selection, and
      // the "Certify All"/"Reject All" bulk CTAs, which all set
      // selectedAccounts + reviewAction and then open this modal), so this one
      // call covers them all.
      lunaSuggestedAccountIds: lunaSuggestedAccountIds(
        effectiveAISuggestions,
        selectedAccounts,
        reviewAction,
      ),
    }).then(() => {
      toast({
        // Never "submitted": a decision is not a submission, and reviewers who
        // read it that way left without submitting (PLO-6222).
        title: "Decision saved",
        description: `Reviewed ${selectedAccounts.length} ${selectedAccounts.length === 1 ? "account" : "accounts"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      // Clear selected accounts first so it propagates to ReviewAccountsTable
      setSelectedAccounts([]);
      setSelectedAccountName(null);
      setMarkAsRemoved(false);
      // Clear flagging state
      setFlaggedSnapshotIds([]);
      closeModal();
      armPrompt();
      // Refetch AI suggestions to update the UI
      refetchAISuggestions();
      // Call the onComplete callback to clear the hidden overlay
      if (bulkActionOnComplete) {
        bulkActionOnComplete();
        setBulkActionOnComplete(null);
      }
    });
  }, [
    reviewAction,
    selectedAccounts,
    reviewId,
    outOfScopeReason,
    note,
    markAsRemoved,
    flaggedSnapshotIds,
    effectiveAISuggestions,
    reviewAccounts,
    toast,
    closeModal,
    refetchAISuggestions,
    bulkActionOnComplete,
    optimisticSubmit,
    accountSetStatus,
    cycleStatus,
    armPrompt,
  ]);

  // Submit account set for approval. In :reviewer_per_set mode the backend
  // expects the confirmed statement IDs alongside the submission so it can
  // create + sign an attestation row in the same transaction. Resolves true
  // only when the submission was accepted, so the modal stays open on a
  // refusal (e.g. an open query) and the reviewer keeps their context.
  const handleSubmitForApproval = useCallback(
    async (statementIds?: string[]): Promise<boolean> => {
      if (!reviewId || isCycleEnded(cycleStatus)) return false;
      try {
        // Business errors (e.g. an OPEN general query, or an undecided account
        // on ANY of the member's sets, blocks the whole submit) ride HTTP 200
        // with { success: false, error } — the request resolves, so we must
        // inspect the body rather than trusting a rejection. Surface the
        // server's human message and DON'T mark the review submitted.
        const result: any = await submitReviewForApproval(
          reviewId,
          statementIds,
        );
        if (result && result.success === false) {
          toast({
            title: "Couldn't submit your decisions",
            description: memberErrorMessage(
              result,
              "Resolve any open queries before submitting.",
            ),
            status: "error",
            duration: 6000,
            isClosable: true,
            position: "bottom-right",
          });
          return false;
        }
        setOptimisticSubmit(optimisticSubmitRecorded);
        return true;
      } catch (error: any) {
        toast({
          title: "Submission failed",
          description: memberErrorMessage(error, "Please try again."),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-right",
        });
        return false;
      }
    },
    [reviewId, cycleStatus, submitReviewForApproval, toast],
  );

  // Handler for when ReviewAccountsTable requests a review modal
  const handleRequestReview = useCallback(
    (params: {
      accountIds: string[];
      action: string;
      suggestion?: string;
      accountName?: string;
    }) => {
      const fallbackAccount =
        params.accountIds.length === 1
          ? flatReviewAccounts.find(
              (account) => account.id === params.accountIds[0],
            )
          : undefined;
      setSelectedAccounts(params.accountIds);
      setSelectedAccountName(
        params.accountName ??
          (fallbackAccount ? reviewAccountDisplayName(fallbackAccount) : null),
      );
      setReviewAction(params.action);
      setOutOfScopeReason("");
      setNote(params.suggestion || "");
      setMarkAsRemoved(false);
      // Reset entitlement flagging state for fresh selection
      setFlaggedSnapshotIds([]);
      openModal();
    },
    [flatReviewAccounts, openModal],
  );

  // Handler for when selection changes in the table
  const handleSelectionChange = useCallback((accountIds: string[]) => {
    setSelectedAccounts(accountIds);
    setSelectedAccountName(null);
  }, []);

  // PLO-4675 — one container, two views: the entitlements table and Luna's
  // Suggestions (which keeps its own bucket pill tabs inside). Legacy ?tab=
  // deep links map onto ?view=. With neither, the org's default view decides
  // the landing (PLO-6504).
  const requestedView = searchParams.get("view") || searchParams.get("tab");
  const currentView = resolveReviewView({
    requested: requestedView,
    orgDefault: currentMember?.accessReviewDefaultView,
  });
  // The org default arrives with the member, after first render. Write the
  // resolved landing into the URL once it is known, so a later refetch can't
  // move the reviewer (and wipe their selection) mid-review.
  useEffect(() => {
    if (requestedView || !currentMember) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set("view", currentView);
    setSearchParams(newParams, { replace: true });
  }, [
    requestedView,
    currentMember,
    currentView,
    searchParams,
    setSearchParams,
  ]);
  // Clear selections when switching views
  useEffect(() => {
    setSelectedAccounts([]);
    setSelectedAccountName(null);
  }, [currentView]);

  const handleViewChange = useCallback(
    (view: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("view", view);
      newParams.delete("tab");
      // Bucket sub-tab selection resets when leaving/entering Suggestions.
      newParams.delete("subtab");
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Luna pitch banner dismissal is per-visit; once every bucketed account is
  // reviewed the remaining-count guard hides it for good.
  const [isLunaPitchDismissed, setIsLunaPitchDismissed] = useState(false);

  const aiFailed = aiStatus === "failed";
  // Accounts Luna has bucketed that are still unreviewed — drives the pitch.
  const totalSuggested =
    getTotalAccountsCount(effectiveAISuggestions.auto_approve) +
    getTotalAccountsCount(effectiveAISuggestions.auto_reject) +
    getTotalAccountsCount(effectiveAISuggestions.needs_review);
  const completedSuggested =
    completionStatus.tabCounts.autoApprove +
    completionStatus.tabCounts.autoReject +
    completionStatus.tabCounts.needsReview;
  const remainingSuggested = Math.max(0, totalSuggested - completedSuggested);

  // Luna's three outcome buckets, rendered inside the Suggestions view by
  // a DS SegmentedControl (the old hand-rolled PillTabsContainer's job). Each
  // segment carries "<reviewed>/<total>" progress; empty buckets are dropped.
  const suggestionBuckets = [
    {
      key: "auto-approve" as const,
      suggestions: effectiveAISuggestions.auto_approve,
      completed: completionStatus.tabCounts.autoApprove,
      isComplete: completionStatus.tabs.autoApprove,
      actionType: "approve" as const,
      onActionAll: handleApproveAll,
    },
    {
      key: "auto-reject" as const,
      suggestions: effectiveAISuggestions.auto_reject,
      completed: completionStatus.tabCounts.autoReject,
      isComplete: completionStatus.tabs.autoReject,
      actionType: "reject" as const,
      onActionAll: handleRejectAll,
    },
    {
      key: "needs-review" as const,
      suggestions: effectiveAISuggestions.needs_review,
      completed: completionStatus.tabCounts.needsReview,
      isComplete: completionStatus.tabs.needsReview,
      actionType: undefined,
      onActionAll: undefined,
    },
  ]
    .map((bucket) => ({
      ...bucket,
      label: SUGGESTION_BUCKET_LABEL[bucket.key],
      total: getTotalAccountsCount(bucket.suggestions),
    }))
    .filter((bucket) => bucket.total > 0);

  const requestedBucket = searchParams.get("subtab") || getDefaultTab();
  const activeBucket =
    suggestionBuckets.find((b) => b.key === requestedBucket) ||
    suggestionBuckets[0];

  const handleBucketChange = useCallback(
    (bucket: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("subtab", bucket);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Expire the optimistic submission as soon as the server reports a new
  // status — the refetch it was covering has landed. This is what lets an
  // admin's revision request reach a reviewer who never left the page.
  useEffect(() => {
    setOptimisticSubmit((prev) =>
      optimisticSubmitObserved(prev, accountSetStatus),
    );
  }, [accountSetStatus]);

  // React Router reuses the ReviewDetail instance when the reviewId param
  // changes, so per-review transient state survives navigation unless we
  // reset it. The optimistic submission is the painful one: attesting Review A
  // leaves it set, and clicking through to Review B (which is still editable)
  // inherits it → table renders locked until a hard refresh.
  useEffect(() => {
    setOptimisticSubmit(noOptimisticSubmit(accountSetStatus));
    setSelectedAccounts([]);
    setBulkActionOnComplete(null);
    // Also clear the ready-to-submit "edit anyway" override so its unlock can't
    // leak into the next review (which would render the table editable while
    // already ready_to_submit).
    setEditOverride(false);
    setSubmitPrompt(idleSubmitPrompt(reviewId));
  }, [reviewId]);

  const filterOptions = useMemo(
    () => [
      { value: "all", label: "All accounts" },
      { value: "unreviewed", label: "Not reviewed" },
      { value: "reviewed", label: "Reviewed" },
      { value: "required", label: "Required" },
      { value: "not_required", label: "Not required" },
      { value: "out_of_scope", label: "Out of scope" },
    ],
    [],
  );

  const combinedReview = isCombinedReview(reviewDetails?.cycle);
  const selectedGrouping =
    groupingChoice ?? (combinedReview ? "person" : "none");

  const groupingOptions = useMemo(
    () => [
      { value: "none", label: "No grouping", icon: XIcon },
      // A combined review's rows are one person's access to many resources;
      // grouping by person puts them under one header to decide together.
      ...(combinedReview
        ? [{ value: "person", label: "Person", icon: UserIcon }]
        : []),
      { value: "department", label: "Department", icon: UsersIcon },
      { value: "jobTitle", label: "Job title", icon: BadgeIcon },
      // Key kept as "accessLevel" so existing grouping state stays valid; it now
      // groups on the account's entitlement names, which is what it always
      // actually read (the old accessLevel field held those same names).
      { value: "accessLevel", label: "Entitlements", icon: KeyIcon },
      { value: "status", label: "Review status", icon: CheckIcon },
    ],
    [combinedReview],
  );

  // Cycle attestation snapshot — present when this review belongs to a cycle.
  // The submit modal branches on `isInlineAttestation` to decide whether to
  // render the attestation step or the simple confirmation copy. Sorting by
  // `order` once here keeps the modal render purely consumptive.
  const cycle = reviewDetails?.cycle ?? null;
  const isInlineAttestation = cycle?.attestationMode === "reviewer_per_set";
  // A decision-only cycle records the reviewer's decision but never
  // deprovisions (PLO-5083). Threaded into the table/modal so they drop the
  // "will deprovision immediately" warning, the consent gate, and the
  // irreversible row-lock — matching the backend, which skips deprovisioning.
  const decisionOnly = cycle?.decisionOnly ?? false;
  // "Allow users to correct data" (PLO-5336). When the campaign turned it off,
  // the reviewer decides against the data as scanned: every "Fix incorrect
  // data" affordance is hidden. The three correction endpoints reject the calls
  // regardless — this is the affordance half of that gate. Absent (older API,
  // or a legacy cycle-less review) means allowed.
  const allowCorrections = cycle?.allowCorrections ?? true;
  // Entitlement-only cycle (PLO-5731): every row is ONE entitlement of one
  // account, and the server lists them flat, so a person with three roles is
  // three rows. Threaded into the table and the decision dialog so they drop
  // the flag-for-adjustment action (the row IS the entitlement) and name the
  // entitlement in the removal copy. Absent on a legacy cycle-less review,
  // which means "standard".
  const entitlementOnly = isEntitlementOnly(cycle);
  const cycleStatements = useMemo(
    () =>
      cycle?.statements
        ? [...cycle.statements].sort((a, b) => a.order - b.order)
        : [],
    [cycle?.statements],
  );
  const requiredStatementIds = useMemo(
    () => cycleStatements.filter((s) => s.required).map((s) => s.id),
    [cycleStatements],
  );
  const allRequiredConfirmed = useMemo(
    () => requiredStatementIds.every((id) => confirmedStatementIds.has(id)),
    [requiredStatementIds, confirmedStatementIds],
  );

  // Reset confirmations whenever the modal closes so a re-open starts fresh.
  // Using an effect (rather than resetting on click of the open trigger)
  // covers the auto-open path and the manual submit button.
  useEffect(() => {
    if (!isSubmitModalOpen) {
      setConfirmedStatementIds(new Set());
    }
  }, [isSubmitModalOpen]);

  const toggleStatement = useCallback((statementId: string) => {
    setConfirmedStatementIds((prev) => {
      const next = new Set(prev);
      if (next.has(statementId)) {
        next.delete(statementId);
      } else {
        next.add(statementId);
      }
      return next;
    });
  }, []);

  const memoizedActiveFilters = useMemo(() => activeFilters, [activeFilters]);

  // ReviewAccountsTable is memo()'d, and these two are the props that used to
  // void it: an inline arrow and an inline `|| []` are new values on every
  // render of this component, so the whole table — every visible row, plus a
  // re-filter of every account in the review — re-rendered on each keystroke in
  // the review modal's note field, which lifts its state up to here. (PLO-5463)
  //
  // setSplitEntitlementsByType takes the next value, not an updater, so the
  // current flag has to stay in the dependency list.
  const handleToggleSplitEntitlements = useCallback(
    () => setSplitEntitlementsByType(!splitEntitlementsByType),
    [setSplitEntitlementsByType, splitEntitlementsByType],
  );
  const tableOutcomeOptions = useMemo(
    () => campaignDetails?.campaign.outcomeOptions ?? EMPTY_OUTCOME_OPTIONS,
    [campaignDetails?.campaign.outcomeOptions],
  );

  // On a re-review, surface a dedicated "Previous review" column (right after
  // Status) carrying each row's earlier-stage decision — WITHOUT mutating the
  // user's saved column config, and never on a single-stage review (column
  // absent entirely). Deduped so a manual toggle can't double-insert it.
  // Same trick for the Access column when the review holds indirect rows — see
  // withAccessKindColumn.
  // Hoisted ABOVE the loading early-return: it's a hook and must run on EVERY
  // render (it reads reviewDetails defensively, nil before the data loads).
  const tableColumnsForRender = useMemo(() => {
    const columns = withResourceColumn(
      withAccessKindColumn(
        visibleColumns,
        hasIndirectAccountRow(flatReviewAccounts),
      ),
      showsResourceColumn(combinedReview, selectedGrouping, visibleColumns),
    );
    const stage = reviewDetails?.accountSetStage ?? 1;
    if (stage <= 1 || columns.includes("previousReview")) {
      return columns;
    }
    const reviewIdx = columns.indexOf("review");
    const at =
      reviewIdx >= 0 ? reviewIdx + 1 : Math.max(0, columns.indexOf("actions"));
    return [...columns.slice(0, at), "previousReview", ...columns.slice(at)];
  }, [
    combinedReview,
    flatReviewAccounts,
    reviewDetails?.accountSetStage,
    selectedGrouping,
    visibleColumns,
  ]);

  // Locking outcomes this cycle configures with NO remediation action — picking
  // one records the decision and nothing else, so its row must stay editable in
  // every review state, not only during a revision request (PLO-5749). Hoisted
  // ABOVE the loading early-return for the same reason as
  // `tableColumnsForRender`: it's a hook and must run on EVERY render.
  const reversibleOutcomes = useMemo(
    () => reversibleOutcomesFor(tableOutcomeOptions),
    [tableOutcomeOptions],
  );

  if (isLoading || !reviewDetails) {
    return (
      <VStack spacing={4} align="flex-start" width="full" p={4}>
        <Button
          variant="subtle"
          tone="neutral"
          size="sm"
          leadingIcon={<Icon as={ArrowLeft} boxSize={4} />}
          onClick={handleGoBack}
        >
          Back to Access Reviews
        </Button>
        <Spinner size="sm" />
      </VStack>
    );
  }

  const { review, stats } = reviewDetails;

  // In a multi-stage re-review the header counter must reflect the CURRENT-stage
  // rows only (the earlier-stage decided rows are folded into "Previously
  // reviewed" and mustn't inflate "X of Y reviewed" / the ring / "N left").
  // The server-fed `stats` count all the reviewer's actionable accounts across
  // stages, so in this context we override them with a client-side count over
  // the loaded rows (we have `account.stage`). Single-stage reviews keep the
  // server stats untouched — they aren't page-limited. See report note.
  const effectiveStats = isMultiStageContext(flatReviewAccounts)
    ? currentStageCounts(flatReviewAccounts)
    : stats;
  // Attestors get a read-only drill-in from the attestation page. They
  // aren't editing the review, just verifying decisions before signing.
  const isAttestorView = reviewDetails.viewMode === "attestor";
  // Gate the lock on `accountSetStatus` (and the optimistic submission while
  // the refetch is in flight) and on the cycle: End Cycle leaves an unsubmitted
  // set `in_progress`, so only `cycleStatus` says the work is over.
  // `review.status` is a roll-up the backend can flip to `ready_for_approval` /
  // `complete` as soon as 100% of accounts have been reviewed — using it here
  // would lock the table the moment the reviewer marked the last row (e.g. bulk
  // Mark Required on All Entitlements), even though they hadn't pressed the
  // submit button yet.
  const isSetLocked =
    isAttestorView ||
    isDecisionLocked(optimisticSubmit, accountSetStatus, cycleStatus);
  // Locks the confirm of every modal that writes, for one that is already open
  // when the refetch reports the cycle ended.
  const cycleEnded = isCycleEnded(cycleStatus);
  // Copy for the tooltip on tools disabled because the set is submitted/locked.
  const setLockedTooltip = cycleEnded
    ? "This review cycle has ended"
    : "Submitted for approval, awaiting review";

  // Admin-authored guidance for this review's resource (PLO-4817) — the
  // Instructions callout in the header card renders iff this is non-null.
  const applicationInstruction = applicationInstructionText(
    review.applicationInstruction,
  );

  // "2nd review" badge (PLO-4806) — non-null only for a later review stage.
  const secondReviewBadge = stageBadgeLabel(reviewDetails.accountSetStage);
  // Re-review banner (PLO-4806) — a stage badge ("Stage 2 of 3") + a one-line
  // sentence naming the reviewer's position. Both null on the implicit first
  // stage, so single-pass reviews render no banner. (The matching "Previous
  // review" column is derived in `tableColumnsForRender`, hoisted above the
  // loading early-return so it stays an unconditional hook.)
  const reReviewBadgeText = reReviewBadge(
    reviewDetails.accountSetStage,
    reviewDetails.stageCount,
  );
  const reReviewBannerSentence = reReviewSentence(
    reviewDetails.accountSetStage,
  );

  // Derived workflow state — single source of truth for the sticky state alert.
  // Uses the effective (current-stage in a re-review) reviewed/total counts so
  // the "ready to submit" flip tracks the rows actually on screen.
  const reviewState = deriveReviewState({
    status: review.status,
    accountSetStatus,
    cycleStatus,
    viewMode: reviewDetails.viewMode,
    reviewedCount: effectiveStats.completed,
    totalCount: effectiveStats.total,
    justSubmitted: isSubmissionPending(optimisticSubmit, accountSetStatus),
  });

  // Non-null only when the reviewer has nothing left in scope (PLO-6038): every
  // row reassigned to someone else or excluded by an admin. They still submit
  // to close the review off, so the banner names why it is empty instead of
  // claiming they reviewed every account.
  const nothingInScopeBanner = emptyScopeBanner(
    reviewState,
    effectiveStats.total,
  );
  // A non-final stage can pass undecided accounts on to the next stage's
  // undecided-rows reviewer, so Submit opens before every row is decided and
  // the confirm modal warns where the blank rows go. The final stage stays
  // strict.
  const canSubmitUndecided = allowsUndecidedSubmission(reviewDetails);
  const undecidedCount = Math.max(
    0,
    effectiveStats.total - effectiveStats.completed,
  );
  const submitCopy = submitConfirmCopy(
    effectiveStats.total,
    isInlineAttestation,
    canSubmitUndecided
      ? { undecidedCount, routing: reviewDetails.undecidedRowsRouting }
      : undefined,
  );
  const canSubmit = canSubmitReview({
    accountSetId,
    accountSetStatus,
    cycleStatus,
    reviewedCount: effectiveStats.completed,
    totalCount: effectiveStats.total,
    allowUndecidedSubmission: canSubmitUndecided,
  });

  // Deadline extension (PLO-4856). The rules + current state ride on the
  // review-details payload; null when the cycle has no rules, they're disabled,
  // or the viewer is an attestor. The effective due date (override || cycle due
  // date) is routed through ONE helper so the header due text, and any future
  // meta, agree with the extension state. Falls back to the campaign cycle due
  // date when there is no wrapper.
  const deadlineExtension = reviewDetails.deadlineExtension ?? null;
  const effectiveDueDate = effectiveReviewDueDate(
    deadlineExtension,
    campaignDetails?.campaign?.dueDate,
  );
  const canRequestExtension = canRequestDeadlineExtension(
    deadlineExtension,
    reviewDetails.viewMode,
  );
  const extensionBannerState = deadlineExtensionBannerState(deadlineExtension);
  const effectiveDueDays = effectiveDueDate
    ? daysUntil(effectiveDueDate)
    : null;

  // Review-scope (account-less) general queries (PLO-4803). An OPEN one shows a
  // "Query awaiting response" chip and blocks submit (server-enforced); once
  // answered (and none still open) a "Response received" chip opens the shared
  // responses modal. Reviewers only — attestors are read-only.
  const generalQueries = reviewDetails.generalQueries ?? [];
  const generalQueryOpen = hasOpenGeneralQuery(generalQueries);
  const generalQueryAnswered = hasAnsweredGeneralQuery(generalQueries);
  const isReviewOverdue =
    reviewIsOverdue(effectiveDueDays, cycleStatus) &&
    campaignDetails?.campaign?.status !== "completed";

  // Which split panel is mounted — resolved through the reviewLogic invariant
  // (corrections wins a tie; at most one renders). `splitPanel` already holds a
  // single selection, but routing it through resolveMountedPanel keeps the
  // single-open invariant the single source of truth.
  const mountedPanel = resolveMountedPanel(
    splitPanel === "corrections",
    splitPanel === "evidence",
  );

  // Ready-to-submit soft-lock (browse-only until the reviewer clicks Edit).
  const softLocked = isSoftLocked(reviewState, editOverride);
  // Confirm on leave only while ready-to-submit — corrections auto-save now, so
  // there are no unsaved fixes to guard.
  leaveGuardRef.current = shouldConfirmLeave(reviewState, false);
  // The reviewer's decision just finished the review: put Submit in front of
  // them (PLO-6222). Set during render, guarded so it runs once — the state
  // it reads is derived below the loading early-return, where no effect can go.
  if (
    submitPromptDue(submitPrompt, {
      reviewId,
      state: reviewState,
      canSubmit,
      editing: editOverride,
    })
  ) {
    setSubmitPrompt({ reviewId, phase: "shown" });
    setIsSubmitModalOpen(true);
  }
  // Badge on the "Fix incorrect data" trigger — the panel's total item count.
  const totalFixCount = recordedFixCount;

  return (
    <Box
      w="full"
      minH="calc(100vh - 50px)"
      display="flex"
      flexDirection="column"
    >
      <VStack
        align="flex-start"
        spacing={6}
        width="full"
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
      >
        {/* Header with navigation. The state alert and the navigator are
            DIRECT children of the page-spanning column on purpose: a sticky
            element can only travel inside its own parent's box, so wrapping
            them in a short header container pinned them for a few pixels and
            then let them scroll away. */}
        {/* Sticky review-state alert — consolidates the revision-requested,
              awaiting-approval and attestor banners into one DS-styled bar that
              takes priority over (sits above) the navigator. Driven by the
              derived reviewState so it never disagrees with the rest of the
              page. Rendered FIRST so it takes the sticky slot; the navigator
              below yields its sticky position while a banner is present. */}
        <ReviewStateAlert
          state={reviewState}
          title={nothingInScopeBanner?.title}
          message={
            reviewState === "revision_requested" && accountSetRevisionMessage
              ? accountSetRevisionMessage
              : nothingInScopeBanner?.message
          }
          action={
            // Nothing in scope means nothing to edit, so the soft-lock's
            // Edit / Done editing toggle is pointless there (PLO-6038).
            reviewState === "ready_to_submit" &&
            accountSetId &&
            !hasNothingInScope(effectiveStats.total) ? (
              softLocked ? (
                <Button
                  variant="subtle"
                  tone="neutral"
                  size="sm"
                  onClick={() => setEditOverride(true)}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  variant="subtle"
                  tone="neutral"
                  size="sm"
                  onClick={() => setEditOverride(false)}
                >
                  Done editing
                </Button>
              )
            ) : undefined
          }
        />

        {/* Re-review banner (PLO-4806) — makes it unmistakable that this is a
              later stage re-reviewing access an earlier reviewer kept. The
              "Previous review" column carries each row's earlier decision. Null
              (hidden) on a first-stage / single-pass review. */}
        {reReviewBannerSentence && (
          <HStack
            data-testid="re-review-banner"
            spacing="sm"
            px={4}
            py={3}
            bg="p.50"
            border="1px solid"
            borderColor="p.200"
            borderRadius="component.xl"
            align="center"
          >
            <Icon as={History} boxSize={4} color="p.700" flexShrink={0} />
            {reReviewBadgeText && (
              <Text
                fontSize="xs"
                fontWeight={700}
                color="p.700"
                bg="p.100"
                px={2}
                py={0.5}
                borderRadius="component.md"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {reReviewBadgeText}
              </Text>
            )}
            <Text fontSize="sm" fontWeight={500} color="p.800" minW={0}>
              {reReviewBannerSentence}
            </Text>
          </HStack>
        )}

        {/* Prominent sticky navigator — icon Back, prev/next resource reviews
              with logos, and centred position indicators. Yields its sticky
              slot to ReviewStateAlert (above) when a banner is present, so we
              never stack two sticky bars. */}
        {reviewId && (campaignDetails?.reviews?.length ?? 0) > 0 && (
          <StickyNavigator
            reviews={campaignDetails!.reviews}
            currentReviewId={reviewId}
            hasBanner={hasReviewStateBanner(reviewState)}
            onBack={handleGoBack}
            onNavigate={handleNavigateReview}
          />
        )}

        {/* Rest of the header. Split from the block above so the sticky
            navigator's containing block is the page-spanning column — a
            sticky element only sticks within its own parent's box, so while
            this content shared a wrapper with it the navigator scrolled away
            as soon as that wrapper ended. */}
        <Box width="full" bg="bg.page" flexShrink={0}>
          {/* Review header — sits directly on the page (no card). */}
          <Box mb="lg">
            <Flex align="flex-start" gap="md" py="lg">
              <Flex align="flex-start" gap="md" flex="1" minW={0}>
                {review.resource.logoUrl || review.resource.imageUrl ? (
                  <AppLogo
                    name={review.resource.name}
                    logoUrl={
                      review.resource.logoUrl || review.resource.imageUrl
                    }
                    size="md"
                  />
                ) : (
                  <Flex
                    w="32px"
                    h="32px"
                    flexShrink={0}
                    align="center"
                    justify="center"
                    borderRadius="component.lg"
                    bg="bg.section"
                  >
                    <ResourceIcon
                      resourceType={review.resource.type}
                      size={4}
                    />
                  </Flex>
                )}
                <VStack align="flex-start" spacing="2xs" minW={0}>
                  <HStack spacing="sm" align="center">
                    <Text
                      fontFamily="heading"
                      fontSize="xl"
                      fontWeight={500}
                      color="text.body.primary"
                      lineHeight="1.2"
                      noOfLines={1}
                    >
                      {appDisplayName(review.resource)}
                    </Text>
                    {secondReviewBadge && (
                      <Text
                        data-testid="second-review-badge"
                        fontSize="xs"
                        fontWeight={600}
                        color="p.700"
                        bg="p.50"
                        px={2}
                        py={0.5}
                        borderRadius="component.md"
                        whiteSpace="nowrap"
                      >
                        {secondReviewBadge}
                      </Text>
                    )}
                  </HStack>
                  {descriptionText(review.resource.description) && (
                    <Text
                      fontSize="sm"
                      color="text.body.secondary"
                      noOfLines={2}
                      data-testid="review-resource-description"
                    >
                      {descriptionText(review.resource.description)}
                    </Text>
                  )}
                  <Text fontSize="sm" color="text.body.tertiary" noOfLines={1}>
                    All users with {appDisplayName(review.resource)} access ·{" "}
                    {review.resource.id}
                  </Text>
                  <HStack spacing="sm" pt="2xs" flexWrap="wrap">
                    <HStack spacing="2xs">
                      <ReviewStatusPill
                        kind={reviewStatePillKind(reviewState)}
                      />
                      {reviewState === "revision_requested" && <RevisionChip />}
                    </HStack>
                    {review.startedAt && (
                      <Text fontSize="sm" color="text.body.tertiary">
                        Started {startedAgoLabel(review.startedAt)}
                        {effectiveDueDate ? " ·" : ""}
                      </Text>
                    )}
                    {effectiveDueDays != null &&
                      (isReviewOverdue ? (
                        <HStack spacing="sm">
                          <OverdueBlip />
                          <Text
                            fontSize="sm"
                            fontWeight={500}
                            color="text.body.error"
                          >
                            {dueDayLabel(effectiveDueDays)}
                          </Text>
                        </HStack>
                      ) : (
                        <Text
                          fontSize="sm"
                          fontWeight={effectiveDueDays <= 3 ? 500 : 400}
                          color={
                            effectiveDueDays <= 3
                              ? "text.body.warning"
                              : "text.body.tertiary"
                          }
                        >
                          {dueDayLabel(effectiveDueDays)}
                        </Text>
                      ))}
                    {canRequestExtension && !isSetLocked && (
                      <Button
                        variant="subtle"
                        tone="primary"
                        size="sm"
                        leadingIcon={<CalendarClock size={14} />}
                        onClick={() => setIsExtensionModalOpen(true)}
                      >
                        Request more time
                      </Button>
                    )}
                    {/* Raise a general (review-scope) query to the campaign
                        admin. Reviewers only — attestors are read-only, and the
                        set is locked once submitted for approval. */}
                    {!isSetLocked && (
                      <Button
                        variant="subtle"
                        tone="neutral"
                        size="sm"
                        leadingIcon={<HelpCircle size={14} />}
                        onClick={() => setIsGeneralQueryModalOpen(true)}
                      >
                        Raise a general query
                      </Button>
                    )}
                    {/* Open general query → awaiting-response chip (also blocks
                        submit, server-enforced). Once answered (none still open)
                        → response-received chip opening the responses modal. */}
                    {generalQueryOpen ? (
                      <QueriedChipVisual
                        label="Query awaiting response"
                        tooltip="You asked the campaign admin about this review. Awaiting their response. You can't submit until it's answered."
                      />
                    ) : (
                      generalQueryAnswered && (
                        <AnsweredChipVisual
                          label="Response received"
                          tooltip="The campaign admin responded to your review query. Open to read it."
                          onClick={() => setIsResponsesModalOpen(true)}
                        />
                      )
                    )}
                  </HStack>
                </VStack>
              </Flex>

              <Box flexShrink={0}>
                {/* Accounts-reviewed progress meter — flips green only when complete. */}
                <ProgressMeter
                  label="Accounts reviewed"
                  value={effectiveStats.completed}
                  total={effectiveStats.total}
                  size="lg"
                  complete={meterIsComplete(reviewState)}
                />
              </Box>
            </Flex>

            {/* Divider under the header */}
            <Box h="1px" bg="border.default" />

            {/* Per-application instructions (PLO-4817) — admin guidance for
                reviewing THIS resource, prominent between the header context
                and the actions. Absent for campaigns without one. Collapsible
                (minus button, top-right) into a compact "View instructions"
                trigger down in the actions row — the padding lives INSIDE the
                Collapse so it animates away too, leaving no residual gap. */}
            {applicationInstruction && (
              <Collapse
                in={!isInstructionsCollapsed}
                animateOpacity
                transition={{
                  enter: {
                    duration: prefersReducedMotion ? 0 : 0.2,
                    ease: "easeOut",
                  },
                  exit: {
                    duration: prefersReducedMotion ? 0 : 0.2,
                    ease: "easeOut",
                  },
                }}
              >
                <Box pt="lg">
                  <InstructionsCallout
                    text={applicationInstruction}
                    onCollapse={() => setInstructionsCollapsed(true)}
                  />
                </Box>
              </Collapse>
            )}

            {extensionBannerState && extensionBannerState !== "granted" && (
              <Box pt="lg">
                <DeadlineExtensionBanner
                  deadlineExtension={deadlineExtension}
                />
              </Box>
            )}

            {/* Actions row — Submit pinned FAR LEFT (always rendered, disabled
                until every account is reviewed / when the review is locked);
                Evidence + Fix incorrect data on the right, each with a count. */}
            <Flex justify="space-between" align="center" gap="md" mt="lg">
              <Tooltip
                label={
                  !accountSetId || isSetLocked
                    ? "This review is locked"
                    : `${Math.max(0, effectiveStats.total - effectiveStats.completed)} review${
                        effectiveStats.total - effectiveStats.completed === 1
                          ? ""
                          : "s"
                      } still to complete`
                }
                isDisabled={canSubmit}
                placement="top"
                hasArrow
              >
                <Box as="span" display="inline-flex">
                  <Button
                    variant="solid"
                    tone="success"
                    size="md"
                    leadingIcon={<Send size={16} />}
                    isLoading={isSubmitting}
                    isDisabled={!canSubmit}
                    onClick={() => setIsSubmitModalOpen(true)}
                  >
                    {SUBMIT_DECISIONS_LABEL}
                  </Button>
                </Box>
              </Tooltip>

              <HStack spacing="sm">
                {/* Compact "View instructions" trigger — only while the
                    Instructions callout above is collapsed; re-expands it
                    (reverse of the Collapse animation above). */}
                {applicationInstruction && isInstructionsCollapsed && (
                  <Button
                    variant="outline"
                    tone="secondary"
                    size="md"
                    leadingIcon={<Icon as={BookOpen} boxSize={4} />}
                    onClick={() => setInstructionsCollapsed(false)}
                  >
                    View instructions
                  </Button>
                )}
                {/* View responses — appears only when the IAM team has answered
                    queries awaiting the reviewer. Amber with a subtle pulse to
                    draw the eye (reduced-motion users get the static styling). */}
                {queryResponses.length > 0 && (
                  <Box
                    as="span"
                    display="inline-flex"
                    borderRadius="component.lg"
                    animation={`${responsePulse} 2.2s ease-in-out infinite`}
                    sx={{
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  >
                    <Button
                      variant="outline"
                      tone="warning"
                      size="md"
                      leadingIcon={<Icon as={MailCheck} boxSize={4} />}
                      trailingIcon={
                        <Counter tone="caution" count={queryResponses.length} />
                      }
                      onClick={() => setIsResponsesModalOpen(true)}
                    >
                      View responses
                    </Button>
                  </Box>
                )}
                {/* Evidence — toggles the evidence split-panel. "Analyse with
                    Luna" lives INSIDE that panel (it can't run without evidence). */}
                <Tooltip
                  label={setLockedTooltip}
                  isDisabled={!isSetLocked}
                  placement="top"
                  hasArrow
                >
                  <Box as="span" display="inline-flex">
                    <Button
                      variant="outline"
                      tone="secondary"
                      size="md"
                      leadingIcon={<Upload size={16} />}
                      trailingIcon={
                        <Counter
                          tone="neutral"
                          count={evidenceFiles?.length ?? 0}
                        />
                      }
                      isDisabled={review.status === "complete" || isSetLocked}
                      onClick={() => toggleSplitPanel("evidence")}
                    >
                      Evidence
                    </Button>
                  </Box>
                </Tooltip>
                {/* Fix incorrect data — opens a menu to pick the kind of data
                    that's wrong/missing (edit entitlements, add a missing user,
                    or view recorded fixes). The button flips solid red while the
                    menu is open. Hidden entirely when the campaign turned
                    corrections off (PLO-5336). */}
                {allowCorrections && (
                  <Menu placement="bottom-end" isLazy>
                    {({ isOpen }) => (
                      <>
                        <Tooltip
                          label={setLockedTooltip}
                          isDisabled={!isSetLocked}
                          placement="top"
                          hasArrow
                        >
                          <MenuButton
                            as={Button}
                            variant={isOpen ? "solid" : "outline"}
                            tone="error"
                            size="md"
                            leadingIcon={<AlertTriangle size={16} />}
                            trailingIcon={
                              <Counter tone="negative" count={totalFixCount} />
                            }
                            isDisabled={
                              review.status === "complete" || isSetLocked
                            }
                          >
                            Fix incorrect data
                          </MenuButton>
                        </Tooltip>
                        <MenuList
                          minW="248px"
                          py="2xs"
                          border="1px solid"
                          borderColor="border.default"
                          boxShadow="overlay"
                        >
                          <MenuItem
                            fontSize="smd"
                            py="sm"
                            iconSpacing="sm"
                            icon={<Icon as={KeyIcon} boxSize={4} />}
                            onClick={() => setIsFixChooserOpen(true)}
                          >
                            Edit an account&rsquo;s entitlements
                          </MenuItem>
                          <MenuItem
                            fontSize="smd"
                            py="sm"
                            iconSpacing="sm"
                            icon={<Icon as={UserPlus} boxSize={4} />}
                            onClick={() => setIsAddAccountModalOpen(true)}
                          >
                            Add a user Ploy is missing
                          </MenuItem>
                          <MenuItem
                            fontSize="smd"
                            py="sm"
                            iconSpacing="sm"
                            icon={<Icon as={UserMinus} boxSize={4} />}
                            onClick={() => setIsRemoveChooserOpen(true)}
                          >
                            Remove a user who doesn&rsquo;t exist
                          </MenuItem>
                          <MenuDivider />
                          <MenuItem
                            fontSize="smd"
                            py="sm"
                            iconSpacing="sm"
                            icon={<Icon as={Eye} boxSize={4} />}
                            isDisabled={totalFixCount === 0}
                            onClick={() => setSplitPanel("corrections")}
                          >
                            View fixes
                          </MenuItem>
                        </MenuList>
                      </>
                    )}
                  </Menu>
                )}
              </HStack>
            </Flex>

            {/* (The uploaded-evidence list now lives INSIDE the evidence
                split-panel — see EvidencePanel below.) */}
          </Box>
        </Box>

        {/* Content + split panels. The evidence / corrections panels dock on the
            right; only one is ever mounted (single-open invariant from
            reviewLogic.resolveMountedPanel). The content column shrinks when a
            panel is open so the table stays fully visible beside it. */}
        <Flex
          align="flex-start"
          gap={mountedPanel ? 4 : 0}
          width="full"
          flex="1"
          minH={0}
        >
          {/* Accounts container */}
          <Box
            pb="20px"
            flex="1"
            minW={0}
            minH="800px"
            display="flex"
            flexDirection="column"
            width="full"
          >
            {/* PLO-4675: Luna pitch — the table is the default landing view,
                so this banner keeps Suggestions discoverable. Hidden once
                the reviewer is on the Suggestions view (it has done its
                job there). */}
            {currentView !== "recommendations" &&
              !aiFailed &&
              !isSetLocked &&
              !isLunaPitchDismissed &&
              (isLunaGenerating || remainingSuggested > 0) && (
                <Box
                  mb="md"
                  flexShrink={0}
                  // elev-low in place of the stroke. Local override rather
                  // than a LunaBanner change — if this look should become a
                  // banner variant, that's a ds edit to make deliberately.
                  sx={{
                    "& > div": {
                      borderColor: "transparent",
                      boxShadow: "elevLow",
                    },
                  }}
                >
                  <LunaBanner
                    title={
                      isLunaGenerating
                        ? "Luna is analyzing this review"
                        : `Review ${remainingSuggested} account${
                            remainingSuggested === 1 ? "" : "s"
                          } in under 5 minutes`
                    }
                    onDismiss={() => setIsLunaPitchDismissed(true)}
                  >
                    {isLunaGenerating ? (
                      <>
                        Suggestions are on their way, so you can start on the
                        entitlements below in the meantime.
                      </>
                    ) : (
                      <Flex direction="column" align="flex-start" gap="sm">
                        <Box as="span">
                          Luna has grouped these accounts by outcome, with
                          reasoning for each group, so you can review whole
                          groups in one click.
                        </Box>
                        <Button
                          variant="solid"
                          tone="luna"
                          size="md"
                          onClick={() => handleViewChange("recommendations")}
                        >
                          Try Suggestions
                        </Button>
                      </Flex>
                    )}
                  </LunaBanner>
                </Box>
              )}
            {/* PLO-4675: one container, two views — a DS SegmentedControl
                switches between the entitlements table (default) and Luna's
                Suggestions, which keeps its own bucket control inside. */}
            <Box
              border="1px solid"
              borderColor="border.default"
              borderRadius="component.2xl"
              bg="bg.container"
              // `clip`, not `hidden`: a hidden overflow makes this panel the
              // stuck review table's scroll container, and it never scrolls,
              // so the table would never stick (PLO-6216).
              overflow="clip"
              width="100%"
              display="flex"
              flexDirection="column"
              flex="1"
              minH={0}
            >
              <Flex
                px="lg"
                py="sm"
                borderBottom="1px solid"
                borderBottomColor="border.default"
                align="center"
                flexShrink={0}
              >
                <SegmentedControl
                  // PLO-5574: the label reads "Suggestions" while the value
                  // stays "recommendations" — it is the URL-visible `?view=`
                  // key, so renaming it would break existing deep links.
                  options={[
                    { value: "entitlements", label: "All entitlements" },
                    { value: "recommendations", label: "Suggestions" },
                  ]}
                  value={currentView}
                  onChange={handleViewChange}
                />
              </Flex>
              {currentView === "recommendations" && (
                <Box
                  p="24px"
                  bg="bg.section"
                  display="flex"
                  flexDirection="column"
                  flex="1"
                  minH={0}
                >
                  {aiFailed ? (
                    // Error state for failed generation
                    <Box
                      width="100%"
                      p={6}
                      borderRadius="12px"
                      border="1px solid"
                      borderColor="red.200"
                      bg="red.50"
                    >
                      <VStack spacing={3} align="center">
                        <Icon as={XCircle} boxSize={8} color="red.500" />
                        <Text fontSize="lg" fontWeight="500" color="red.700">
                          AI Generation Failed
                        </Text>
                        <Text fontSize="sm" color="red.600" textAlign="center">
                          {aiErrorMessage ||
                            "Failed to generate AI suggestions. Please try refreshing the page."}
                        </Text>
                      </VStack>
                    </Box>
                  ) : isLunaGenerating ? (
                    // Luna is generating suggestions loading state
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      minHeight="400px"
                      width="100%"
                    >
                      <motion.div
                        initial={{
                          opacity: 0,
                          y: 30,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        transition={{
                          duration: 0.5,
                          ease: [0.4, 0, 0.2, 1],
                        }}
                        style={{
                          width: "100%",
                          maxWidth: "500px",
                        }}
                      >
                        <Box
                          bg="bg.container"
                          borderRadius="component.2xl"
                          border="1px solid"
                          borderColor="border.default"
                          boxShadow="elevLow"
                          p={8}
                          width="100%"
                        >
                          <VStack spacing={6}>
                            {/* Loading text with Luna icon */}
                            <VStack spacing={2} align="center">
                              <HStack spacing={3} align="center">
                                {/* The mark runs its `acting` state
                                              while Luna is actually working —
                                              the lunar cycle IS the progress
                                              signal, so it must not be static
                                              here. */}
                                <Box display="flex" alignItems="center">
                                  <LunaMark size={40} state="acting" />
                                </Box>

                                <Text
                                  fontSize="2xl"
                                  fontWeight="500"
                                  color="black"
                                >
                                  Luna is analyzing
                                </Text>
                              </HStack>

                              <Text fontSize="sm" color="gray.600" mt={-1}>
                                {aiStatus === "not_found"
                                  ? "Starting AI generation..."
                                  : "Suggestions will be available shortly..."}
                              </Text>
                            </VStack>

                            {/* Progress indicator */}
                            <Box width="320px" position="relative">
                              <Box
                                height="6px"
                                bg="gray.100"
                                borderRadius="full"
                                overflow="hidden"
                                position="relative"
                                boxShadow="inset 0 1px 3px rgba(0, 0, 0, 0.08)"
                              >
                                {/* Shimmer effect background */}
                                <motion.div
                                  style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background:
                                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                                    backgroundSize: "200% 100%",
                                  }}
                                  animate={{
                                    backgroundPosition: ["200% 0", "-200% 0"],
                                  }}
                                  transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "linear",
                                  }}
                                />

                                {/* Main loading bar */}
                                <motion.div
                                  style={{
                                    height: "100%",
                                    width: "240px",
                                    background:
                                      "linear-gradient(135deg, #E98B54 0%, #E66565 50%, #ED6FDA 100%)",
                                    borderRadius: "999px",
                                    position: "relative",
                                    boxShadow:
                                      "0 2px 8px rgba(230, 101, 101, 0.4)",
                                  }}
                                  animate={{
                                    x: [-60, 140, -60],
                                  }}
                                  transition={{
                                    x: {
                                      duration: 1.8,
                                      repeat: Infinity,
                                      ease: "easeInOut",
                                      times: [0, 0.5, 1],
                                    },
                                  }}
                                >
                                  {/* Glow effect */}
                                  <motion.div
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      transform: "translate(-50%, -50%)",
                                      width: "120%",
                                      height: "200%",
                                      background:
                                        "radial-gradient(ellipse at center, rgba(237, 111, 218, 0.3), transparent)",
                                      filter: "blur(8px)",
                                    }}
                                    animate={{
                                      opacity: [0.5, 1, 0.5],
                                    }}
                                    transition={{
                                      duration: 1.5,
                                      repeat: Infinity,
                                      ease: "easeInOut",
                                    }}
                                  />
                                </motion.div>
                              </Box>
                            </Box>
                          </VStack>
                        </Box>
                      </motion.div>
                    </Flex>
                  ) : activeBucket ? (
                    <Flex direction="column" flex="1" minH={0}>
                      <Flex
                        justify="center"
                        mb="md"
                        flexShrink={0}
                        // This control sits on the tinted
                        // Suggestions panel (bg.section), where
                        // the standard neutral.100 track disappears
                        // — one step darker so it still reads as a
                        // control. Local to this surface: the
                        // segmented.bg token stays as upstream.
                        sx={{ "& > div": { bg: "neutral.200" } }}
                      >
                        <SegmentedControl
                          options={suggestionBuckets.map((b) => ({
                            value: b.key,
                            label: b.label,
                            count: `${b.completed}/${b.total}`,
                            countTone: b.isComplete
                              ? ("positive" as const)
                              : ("caution" as const),
                          }))}
                          value={activeBucket.key}
                          onChange={handleBucketChange}
                        />
                      </Flex>
                      {!accountRows ? (
                        <Flex justify="center" align="center" minHeight="400px">
                          <Spinner size="lg" />
                        </Flex>
                      ) : (
                        <CardCarousel
                          items={activeBucket.suggestions.map((item, idx) => ({
                            id: `${activeBucket.key}-${idx}`,
                            description:
                              item.reasoning || "No reasoning provided",
                            accountIds: item.account_ids,
                            reviewId: reviewId || "",
                            actionType: activeBucket.actionType,
                            isComplete:
                              completionStatus.cards[
                                `${activeBucket.key}-${idx}`
                              ] || false,
                          }))}
                          onActionAll={activeBucket.onActionAll}
                          isActioning={isReviewing}
                          selectedAccounts={selectedAccounts}
                          isResourceOwner={isResourceOwner}
                          decisionOnly={decisionOnly}
                          reversibleOutcomes={reversibleOutcomes}
                          // Recommendations embeds the same review
                          // table, so the corrections setting and
                          // its persist handler have to reach it
                          // too (PLO-5575).
                          allowCorrections={allowCorrections}
                          onApplyCorrections={applyCorrections}
                          outcomeOptions={
                            campaignDetails?.campaign.outcomeOptions
                          }
                          onRequestReview={handleRequestReview}
                        />
                      )}
                    </Flex>
                  ) : null}
                </Box>
              )}
              {currentView === "entitlements" && (
                <Box
                  bg="bg.container"
                  p="lg"
                  display="flex"
                  flexDirection="column"
                  flex="1"
                  minH={0}
                >
                  {/* Controls & Advanced Filtering */}
                  <Flex
                    mb="md"
                    align="center"
                    justify="space-between"
                    flexWrap={{ base: "wrap", md: "nowrap" }}
                    gap="sm"
                  >
                    <HStack
                      spacing="sm"
                      align="center"
                      flex={{ base: "1 0 100%", lg: "0 1 auto" }}
                      minW={0}
                    >
                      <Box
                        w={{ base: "full", md: "260px" }}
                        minW={{ base: "220px", md: "260px" }}
                        flexShrink={0}
                      >
                        <SearchInput
                          placeholder="Search accounts…"
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onClear={() => setSearchQuery("")}
                          aria-label="Search accounts"
                        />
                      </Box>
                      <FilterBar
                        onAddFilter={handleAddFilter}
                        availableFilters={getAvailableFilterTypes()}
                        customFilterOptions={getOptionsForFilter}
                      />
                    </HStack>

                    <HStack
                      spacing="sm"
                      align="center"
                      flex={{ base: "1 0 100%", md: "0 0 auto" }}
                      justify={{ base: "flex-start", md: "flex-end" }}
                      flexWrap="wrap"
                    >
                      {/* Hide-reviewed toggle — review STATE control, split
                            from the review STATUS filter. Disabled until at
                            least one account is reviewed. */}
                      <Tooltip
                        label={
                          canHideReviewed
                            ? undefined
                            : "Review some accounts first"
                        }
                        isDisabled={canHideReviewed}
                        hasArrow
                        placement="top"
                      >
                        <HStack
                          spacing="xs"
                          align="center"
                          opacity={canHideReviewed ? 1 : 0.5}
                        >
                          <DsSwitch
                            checked={hideReviewed}
                            onChange={setHideReviewed}
                            disabled={!canHideReviewed}
                            aria-label="Hide reviewed accounts"
                          />
                          <Text
                            fontSize="xs"
                            fontWeight={500}
                            color="text.body.tertiary"
                            whiteSpace="nowrap"
                          >
                            Hide reviewed accounts
                          </Text>
                        </HStack>
                      </Tooltip>

                      {/* Hide-removed toggle (PLO-4806) — default ON. Sibling
                              of hide-reviewed; disabled until something is
                              removed. Display-only, counts unchanged. */}
                      <Tooltip
                        label={
                          canHideRemoved
                            ? undefined
                            : "No removed accounts to hide"
                        }
                        isDisabled={canHideRemoved}
                        hasArrow
                        placement="top"
                      >
                        <HStack
                          spacing="xs"
                          align="center"
                          opacity={canHideRemoved ? 1 : 0.5}
                        >
                          <DsSwitch
                            checked={hideRemoved}
                            onChange={setHideRemoved}
                            disabled={!canHideRemoved}
                            aria-label="Hide removed accounts"
                          />
                          <Text
                            fontSize="xs"
                            fontWeight={500}
                            color="text.body.tertiary"
                            whiteSpace="nowrap"
                          >
                            Hide removed accounts
                          </Text>
                        </HStack>
                      </Tooltip>

                      <DropdownSelector
                        label="Status"
                        icon={CircleCheckIcon}
                        value={selectedFilter}
                        options={filterOptions}
                        onChange={setSelectedFilter}
                      />

                      <DropdownSelector
                        label="Group by"
                        icon={ListCollapseIcon}
                        value={selectedGrouping}
                        options={groupingOptions}
                        onChange={setSelectedGrouping}
                      />

                      {/* Column configuration dropdown */}
                      <Box position="relative" ref={columnsRef}>
                        <Button
                          variant="outline"
                          tone="neutral"
                          size="md"
                          leadingIcon={<Icon as={FiColumns} boxSize={4} />}
                          trailingIcon={
                            <Counter
                              count={visibleColumns.length}
                              tone="neutral"
                            />
                          }
                          onClick={() => setIsColumnsOpen(!isColumnsOpen)}
                          aria-label="Configure visible columns"
                        >
                          Columns
                        </Button>

                        {isColumnsOpen && (
                          <Box
                            position="absolute"
                            top="calc(100% + 4px)"
                            right={0}
                            width="220px"
                            zIndex="dropdown"
                            bg="bg.container"
                            boxShadow="elevHigh"
                            borderRadius="component.lg"
                            border="1px solid"
                            borderColor="border.default"
                            py="2xs"
                          >
                            {columnOptions.map((option) => (
                              <HStack
                                key={option.value}
                                px="sm"
                                py="xs"
                                _hover={{ bg: "bg.section" }}
                                cursor="pointer"
                                onClick={() => toggleColumn(option.value)}
                              >
                                <Box
                                  w="16px"
                                  h="16px"
                                  border="1px solid"
                                  borderColor={
                                    visibleColumns.includes(option.value)
                                      ? "p.600"
                                      : "border.default"
                                  }
                                  bg={
                                    visibleColumns.includes(option.value)
                                      ? "p.50"
                                      : "bg.container"
                                  }
                                  borderRadius="component.sm"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {visibleColumns.includes(option.value) && (
                                    <Icon
                                      as={CheckIcon}
                                      color="p.600"
                                      boxSize="10px"
                                    />
                                  )}
                                </Box>
                                <Text
                                  fontSize="xs"
                                  color="text.body.primary"
                                  fontWeight={500}
                                >
                                  {option.label}
                                </Text>
                              </HStack>
                            ))}

                            {/* Split entitlements by type toggle */}
                            {hasRealEntitlementTypes(
                              uniqueEntitlementTypes,
                            ) && (
                              <>
                                <Box
                                  borderTop="1px solid"
                                  borderColor="border.default"
                                  my="2xs"
                                />
                                <HStack
                                  px="sm"
                                  py="xs"
                                  _hover={{ bg: "bg.section" }}
                                  cursor="pointer"
                                  onClick={() =>
                                    setSplitEntitlementsByType(
                                      !splitEntitlementsByType,
                                    )
                                  }
                                >
                                  <Box
                                    w="16px"
                                    h="16px"
                                    border="1px solid"
                                    borderColor={
                                      splitEntitlementsByType
                                        ? "p.600"
                                        : "border.default"
                                    }
                                    bg={
                                      splitEntitlementsByType
                                        ? "p.50"
                                        : "bg.container"
                                    }
                                    borderRadius="component.sm"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    {splitEntitlementsByType && (
                                      <Icon
                                        as={CheckIcon}
                                        color="p.600"
                                        boxSize="10px"
                                      />
                                    )}
                                  </Box>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="500"
                                    color="text.body.primary"
                                  >
                                    Split by type
                                  </Text>
                                </HStack>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </HStack>
                  </Flex>

                  {/* Filter chips row - placed below the controls */}
                  {activeFilters.length > 0 && (
                    <FilterChips
                      activeFilters={activeFilters}
                      onRemoveFilter={handleRemoveFilter}
                      onClearAll={handleClearAllFilters}
                    />
                  )}

                  {/* Review accounts table */}
                  {reviewId && (
                    <ReviewAccountsTable
                      reviewId={reviewId}
                      searchQuery={searchQuery}
                      filterType={selectedFilter}
                      groupBy={selectedGrouping}
                      advancedFilters={memoizedActiveFilters}
                      outcomeOptions={tableOutcomeOptions}
                      visibleColumns={tableColumnsForRender}
                      fillHeight={true}
                      isResourceOwner={isResourceOwner}
                      decisionOnly={decisionOnly}
                      reversibleOutcomes={reversibleOutcomes}
                      onRequestReview={handleRequestReview}
                      onReviewSubmitted={armPrompt}
                      preSelectedAccounts={selectedAccounts}
                      onSelectionChange={handleSelectionChange}
                      splitEntitlementsByType={splitEntitlementsByType}
                      // Omitted, which hides the header's split control,
                      // when nothing in this review carries a real type —
                      // splitting there yields a lone "Other" column, so
                      // the control would do nothing (PLO-5119).
                      onToggleSplitEntitlements={
                        hasRealEntitlementTypes(uniqueEntitlementTypes)
                          ? handleToggleSplitEntitlements
                          : undefined
                      }
                      isReadOnly={isSetLocked || softLocked}
                      hideReviewed={hideReviewed}
                      hideRemoved={hideRemoved}
                      canEditEntitlements={mountedPanel === "corrections"}
                      allowCorrections={allowCorrections}
                      onApplyCorrections={applyCorrections}
                      entitlementOnly={entitlementOnly}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Right-docked split panels — exactly one is mounted at a time. */}
          {mountedPanel === "evidence" && (
            <Box
              width="400px"
              flexShrink={0}
              position="sticky"
              top={2}
              alignSelf="stretch"
              minH="600px"
            >
              <EvidencePanel
                files={evidenceFiles || []}
                uploads={evidenceUploads}
                isDownloading={getDownloadUrl.isLoading}
                isDeleting={deleteEvidence.isLoading}
                canAnalyse={!!accountSetId && (evidenceFiles?.length ?? 0) > 0}
                hasExistingAnalysis={!!existingAnalysis}
                readOnly={review.status === "complete" || isSetLocked}
                onUpload={handleEvidenceUpload}
                onRetryUpload={handleRetryUpload}
                onDismissUpload={handleDismissUpload}
                onAnalyse={() => setIsLunaModalOpen(true)}
                onDownload={handleEvidenceDownload}
                onDelete={handleEvidenceDelete}
                onClose={() => setSplitPanel(null)}
              />
            </Box>
          )}
          {mountedPanel === "corrections" && (
            <Box
              width="400px"
              flexShrink={0}
              position="sticky"
              top={2}
              alignSelf="stretch"
              minH="600px"
            >
              <FixPanel
                accounts={flatReviewAccounts}
                readOnly={
                  review.status === "complete" ||
                  isSetLocked ||
                  !allowCorrections
                }
                onAddUser={() => setIsAddAccountModalOpen(true)}
                onClose={() => setSplitPanel(null)}
              />
            </Box>
          )}
        </Flex>
      </VStack>

      {/* Add Account Modal */}
      {reviewId && review.resource.id && (
        <AddAccountModal
          isOpen={isAddAccountModalOpen}
          onClose={() => setIsAddAccountModalOpen(false)}
          reviewId={reviewId}
          resourceId={review.resource.id}
          isLocked={cycleEnded}
          resourceOptions={
            combinedReview
              ? reviewResourceOptions(flatReviewAccounts)
              : undefined
          }
        />
      )}

      {/* Header-menu "Edit an account's entitlements": pick an account, then
          stage a correction for it (no persist — lands in the panel). */}
      <AccountChooserModal
        isOpen={isFixChooserOpen}
        onClose={() => setIsFixChooserOpen(false)}
        accounts={flatReviewAccounts}
        onSelect={(accountId) => {
          setFixTargetAccountId(accountId);
          setIsFixChooserOpen(false);
        }}
      />
      <FixIncorrectDataModal
        isOpen={fixTargetAccountId !== null}
        onClose={() => setFixTargetAccountId(null)}
        selectedAccounts={fixTargetAccounts}
        onApply={applyCorrections}
        isLocked={cycleEnded}
      />

      {/* IAM query responses awaiting the reviewer's re-decision. */}
      <QueryResponsesModal
        isOpen={isResponsesModalOpen}
        onClose={() => setIsResponsesModalOpen(false)}
        entries={queryResponses}
      />

      {/* Raise a general (review-scope, account-less) query to the campaign
          admin — mandatory note, review-level copy. */}
      <QueryModal
        isOpen={isGeneralQueryModalOpen}
        onClose={() => setIsGeneralQueryModalOpen(false)}
        subtitle="About this review"
        placeholder="Ask the campaign admin about this review…"
        isSubmitting={isRaisingGeneralQuery}
        isLocked={cycleEnded}
        onSubmit={handleRaiseGeneralQuery}
      />

      {/* "Remove a user who doesn't exist": pick an account, confirm, then mark
          it already-removed / access revoked at source. Already-removed accounts
          are excluded — the correction can't apply to them twice. */}
      <AccountChooserModal
        isOpen={isRemoveChooserOpen}
        onClose={() => setIsRemoveChooserOpen(false)}
        accounts={accountsSelectableForRemoval(flatReviewAccounts)}
        onSelect={(accountId) => {
          setRemoveTargetAccountId(accountId);
          setIsRemoveChooserOpen(false);
        }}
      />
      {removeTargetAccount && !cycleEnded && (
        <ConfirmModal
          title="Remove a user who doesn't exist?"
          body={`This records that ${reviewAccountDisplayName(
            removeTargetAccount,
          )} doesn't have this access at source. Their account is marked already removed and the access is recorded as revoked, so it drops out of this review. This fixes incorrect data; it is not a review decision.`}
          confirmLabel="Remove account"
          confirmTone="danger"
          onConfirm={() => handleRemoveNonExistent()}
          onCancel={() => setRemoveTargetAccountId(null)}
        />
      )}

      {/* (The recorded-fixes audit view now lives inside FixPanel's
          auditing state — see the corrections split-panel above.) */}

      {/* Leave-confirmation when navigating away un-submitted (ready-to-submit)
          or with an unsaved Fix-incorrect-data draft (F2). Covers every
          navigation path — Back, StickyNavigator prev/next, browser
          back/forward, and any other in-app link — via the router-level
          useBlocker guard above. */}
      {blocker.state === "blocked" &&
        (() => {
          const copy = leaveConfirmCopy(
            reviewState,
            false,
            effectiveStats.total,
          );
          return (
            <ConfirmModal
              title={copy.title}
              body={copy.body}
              confirmLabel={copy.confirmLabel}
              confirmTone="primary"
              onConfirm={() => blocker.proceed()}
              onCancel={() => blocker.reset()}
            />
          );
        })()}

      {/* Review Modal for bulk actions */}
      <ReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          closeModal();
          setSelectedAccounts([]);
          setSelectedAccountName(null);
          setSuggestionReasoning(null);
          setMarkAsRemoved(false);
          // Clear flagging state
          setFlaggedSnapshotIds([]);
        }}
        reviewAction={reviewAction}
        selectedCount={selectedAccounts.length}
        selectedAccountName={selectedAccountName ?? undefined}
        outOfScopeReason={outOfScopeReason}
        setOutOfScopeReason={setOutOfScopeReason}
        note={note}
        setNote={setNote}
        onSubmit={submitReview}
        isSubmitting={isReviewing}
        outcomeOptions={
          campaignDetails?.campaign.outcomeOptions || [
            { name: "Required", standard_mapping: "required" },
            // Fails closed until the cycle's real outcomes load: without an
            // action list this reads as "removes nothing", which would drop
            // the de-provisioning warning (PLO-5804).
            {
              name: "Not Required",
              standard_mapping: "not_required",
              actions: [{ type: "deprovision" }],
            },
          ]
        }
        isResourceOwner={isResourceOwner}
        markAsRemoved={markAsRemoved}
        setMarkAsRemoved={setMarkAsRemoved}
        decisionOnly={decisionOnly}
        // Entitlement flagging props
        entitlements={selectedAccountsEntitlements}
        accountEntitlementIds={selectedAccountsEntitlementIds}
        grantingGroups={selectedGrantingGroups.groups}
        adjustSeparately={selectedGrantingGroups.separate}
        flaggedSnapshotIds={flaggedSnapshotIds}
        setFlaggedSnapshotIds={setFlaggedSnapshotIds}
        entitlementOnly={entitlementOnly}
        pendingRemovalCount={selectedPendingRemovals}
      />

      {/* Luna Analysis Modal */}
      <LunaAnalysisModal
        isOpen={isLunaModalOpen}
        onClose={() => setIsLunaModalOpen(false)}
        reviewId={reviewId || ""}
        accountSetId={accountSetId}
        isLocked={review.status === "complete" || isSetLocked}
      />

      {/* Request-more-time Modal (PLO-4856). Only mounted when the cycle has
          extension rules for this reviewer and there's an account set to
          request against. On auto-grant, the mutation's query invalidation
          refetches the review-details payload so the header due text reflects
          the new effective due date. */}
      {deadlineExtension && accountSetId && (
        <RequestExtensionModal
          isOpen={isExtensionModalOpen}
          onClose={() => setIsExtensionModalOpen(false)}
          accountSetId={accountSetId}
          deadlineExtension={deadlineExtension}
          isLocked={cycleEnded}
        />
      )}

      {/* Submit modal.
          Two shapes share this modal:
            1. :reviewer_per_set → expanded attestation step (statement list)
            2. everything else  → simple "all reviewed" confirmation
          The Submit handler in (1) attaches the confirmed statement IDs so the
          backend can create + sign the attestation row in the same call. */}
      <Modal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" backdropFilter="blur(3px)" />
        <ModalContent
          borderRadius="16px"
          maxW={
            isInlineAttestation
              ? { base: "calc(100% - 32px)", md: "560px" }
              : "440px"
          }
          mx={{ base: 4, md: 0 }}
        >
          <ModalCloseButton size="sm" />
          <ModalHeader pb={2}>
            <HStack spacing={3} align="center">
              <Flex
                w="40px"
                h="40px"
                borderRadius="10px"
                bg={
                  isInlineAttestation
                    ? "p.50"
                    : submitCopy.warning
                      ? "warning.50"
                      : "green.50"
                }
                border="1px solid"
                borderColor={
                  isInlineAttestation
                    ? "p.100"
                    : submitCopy.warning
                      ? "warning.200"
                      : "green.100"
                }
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon
                  as={
                    isInlineAttestation
                      ? CircleCheckIcon
                      : submitCopy.warning
                        ? AlertTriangle
                        : CheckCircle
                  }
                  boxSize={5}
                  color={
                    isInlineAttestation
                      ? "p.600"
                      : submitCopy.warning
                        ? "warning.500"
                        : "green.500"
                  }
                />
              </Flex>
              <Text
                fontFamily="HubotSans, sans-serif"
                fontSize="20px"
                fontWeight="500"
                color="text.body.primary"
                lineHeight="1.3"
              >
                {submitCopy.title}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalBody pt={0} pb={4}>
            {/* Undecided accounts move on to the next stage (non-final stage
                with the campaign setting on). Above both shapes so the
                reviewer reads it before signing or submitting. */}
            {submitCopy.warning && (
              <HStack
                align="flex-start"
                spacing={2.5}
                mb={4}
                px={3}
                py={2.5}
                bg="warning.50"
                border="1px solid"
                borderColor="warning.200"
                borderRadius="10px"
                role="alert"
              >
                <Icon
                  as={AlertTriangle}
                  boxSize={4}
                  color="warning.500"
                  mt="2px"
                  flexShrink={0}
                />
                <Text
                  fontSize="13px"
                  color="warning.700"
                  lineHeight="1.5"
                  sx={{ textWrap: "pretty" }}
                >
                  {submitCopy.warning}
                </Text>
              </HStack>
            )}
            {isInlineAttestation ? (
              <VStack align="stretch" spacing={4}>
                <Text
                  fontSize="13px"
                  color="text.body.secondary"
                  lineHeight="1.5"
                  sx={{ textWrap: "pretty" }}
                >
                  {submitCopy.body}
                </Text>

                {/* Progress indicator: confirmed-required / total-required.
                    Gives the user immediate feedback as they tick boxes
                    and surfaces the gating constraint without a wall of
                    text. */}
                {requiredStatementIds.length > 0 && (
                  <HStack
                    spacing={3}
                    align="center"
                    px={3}
                    py={2.5}
                    bg="bg.subtle"
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="10px"
                  >
                    <Box flex="1" minW={0}>
                      <Box
                        position="relative"
                        h="4px"
                        w="full"
                        bg="border.default"
                        borderRadius="full"
                        overflow="hidden"
                      >
                        <Box
                          position="absolute"
                          left={0}
                          top={0}
                          bottom={0}
                          bg={allRequiredConfirmed ? "success.500" : "p.500"}
                          borderRadius="full"
                          width={`${
                            (requiredStatementIds.filter((id) =>
                              confirmedStatementIds.has(id),
                            ).length /
                              requiredStatementIds.length) *
                            100
                          }%`}
                          transitionProperty="width, background-color"
                          transitionDuration="150ms"
                          transitionTimingFunction="ease-out"
                        />
                      </Box>
                    </Box>
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color={
                        allRequiredConfirmed
                          ? "success.700"
                          : "text.body.secondary"
                      }
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      whiteSpace="nowrap"
                    >
                      {
                        requiredStatementIds.filter((id) =>
                          confirmedStatementIds.has(id),
                        ).length
                      }{" "}
                      of {requiredStatementIds.length} required
                    </Text>
                  </HStack>
                )}

                <VStack
                  align="stretch"
                  spacing={2.5}
                  role="group"
                  aria-label="Attestation statements"
                >
                  {cycleStatements.map((s, i) => (
                    <StatementCard
                      key={s.id}
                      text={s.text}
                      isRequired={s.required}
                      isChecked={confirmedStatementIds.has(s.id)}
                      onToggle={() => toggleStatement(s.id)}
                      index={i + 1}
                    />
                  ))}
                </VStack>
              </VStack>
            ) : (
              <Text
                fontSize="13px"
                color="text.body.secondary"
                lineHeight="1.5"
              >
                {submitCopy.body}
              </Text>
            )}
          </ModalBody>
          <ModalFooter pt={0} gap={3}>
            <Button
              variant="subtle"
              tone="neutral"
              onClick={() => setIsSubmitModalOpen(false)}
            >
              Submit later
            </Button>
            <Button
              variant="solid"
              tone="success"
              leadingIcon={<Send size={16} />}
              isDisabled={
                cycleEnded || (isInlineAttestation && !allRequiredConfirmed)
              }
              onClick={async () => {
                // Snapshot the confirmed IDs (set order is insertion order,
                // but server treats the array as a set anyway).
                const statementIds = isInlineAttestation
                  ? Array.from(confirmedStatementIds)
                  : undefined;
                if (await handleSubmitForApproval(statementIds)) {
                  setIsSubmitModalOpen(false);
                }
              }}
              isLoading={isSubmitting}
            >
              {isInlineAttestation ? "Attest & Submit" : SUBMIT_DECISIONS_LABEL}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default memo(ReviewDetail);
